/* Boni App — compiled from JSX. DO NOT hand-edit. Run scripts/build-web.sh */

// Icon — inlines a Lucide SVG (stroke = currentColor) and caches it.
const __iconCache = {};
function Icon({
  name,
  size = 20,
  strokeWidth = 2,
  color = 'currentColor',
  style = {}
}) {
  const [svg, setSvg] = React.useState(__iconCache[name] || '');
  React.useEffect(() => {
    let alive = true;
    if (__iconCache[name]) {
      setSvg(__iconCache[name]);
      return;
    }
    fetch(`https://unpkg.com/lucide-static@0.484.0/icons/${name}.svg`).then(r => r.ok ? r.text() : '').then(t => {
      if (!t) return;
      t = t.replace(/width="24"/, '').replace(/height="24"/, '');
      __iconCache[name] = t;
      if (alive) setSvg(t);
    }).catch(() => {});
    return () => {
      alive = false;
    };
  }, [name]);
  return /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    style: {
      display: 'inline-flex',
      width: size,
      height: size,
      color,
      lineHeight: 0,
      flex: 'none',
      ...style
    },
    dangerouslySetInnerHTML: {
      __html: svg.replace('<svg', `<svg width="${size}" height="${size}" stroke-width="${strokeWidth}"`)
    }
  });
}
window.Icon = Icon;

// DoodleIcon — hand-drawn, sticker-style icons unique to Boni. `color` is the
// flavour accent; outlines stay ink. Falls back to a Lucide icon if unknown.
function DoodleIcon({
  name,
  size = 40,
  color = 'var(--ink-800)',
  style = {}
}) {
  const INK = 'var(--ink-800)';
  const CREAM = 'var(--cream-50)';
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 48 48',
    fill: 'none',
    'aria-hidden': 'true',
    style: {
      display: 'block',
      overflow: 'visible',
      ...style
    }
  };
  switch (name) {
    case 'dog':
      // tell us about your dog
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("path", {
        d: "M13 13 C 8 10 6.5 18 11 22.5 L 17.5 16 Z",
        fill: color,
        stroke: INK,
        strokeWidth: "2.4",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M35 13 C 40 10 41.5 18 37 22.5 L 30.5 16 Z",
        fill: color,
        stroke: INK,
        strokeWidth: "2.4",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M24 12 C 32 12 38 18 38 26 C 38 33 31.5 38.5 24 38.5 C 16.5 38.5 10 33 10 26 C 10 18 16 12 24 12 Z",
        fill: CREAM,
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "19",
        cy: "25",
        r: "2.2",
        fill: INK
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "29",
        cy: "25",
        r: "2.2",
        fill: INK
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "24",
        cy: "30.5",
        rx: "3",
        ry: "2.3",
        fill: INK
      }), /*#__PURE__*/React.createElement("path", {
        d: "M24 32.6 C 24 35 21.6 35.4 20.6 34.4",
        stroke: INK,
        strokeWidth: "2",
        strokeLinecap: "round"
      }));
    case 'match':
      // your perfect bowl
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("path", {
        d: "M9.5 24 C 10.5 32.5 15 38.5 24 38.5 C 33 38.5 37.5 32.5 38.5 24",
        fill: CREAM,
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinejoin: "round",
        strokeLinecap: "round"
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "24",
        cy: "24",
        rx: "15",
        ry: "4",
        fill: color,
        stroke: INK,
        strokeWidth: "2.6"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M24 8 C 21.5 4.5 16 6 16 10 C 16 13.2 24 16.8 24 16.8 C 24 16.8 32 13.2 32 10 C 32 6 26.5 4.5 24 8 Z",
        fill: color,
        stroke: INK,
        strokeWidth: "2.4",
        strokeLinejoin: "round"
      }));
    case 'clock':
      // she keeps count of the days for you
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("circle", {
        cx: "24",
        cy: "27",
        r: "15",
        fill: color,
        stroke: INK,
        strokeWidth: "2.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "24",
        cy: "27",
        r: "10",
        fill: CREAM,
        stroke: INK,
        strokeWidth: "2.2"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M24 27 L 24 20.5",
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinecap: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M24 27 L 29.5 29.5",
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinecap: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M20 8.5 L 24 11.8 L 28 8.5",
        fill: "none",
        stroke: INK,
        strokeWidth: "2.4",
        strokeLinejoin: "round",
        strokeLinecap: "round"
      }));
    case 'box':
      // arrives monthly
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("path", {
        d: "M24 9 L 38 16 L 38 32 L 24 39 L 10 32 L 10 16 Z",
        fill: color,
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M10 16 L 24 23 L 38 16 M24 23 L 24 39",
        stroke: INK,
        strokeWidth: "2.4",
        strokeLinejoin: "round",
        strokeLinecap: "round",
        fill: "none"
      }), /*#__PURE__*/React.createElement("g", {
        fill: CREAM
      }, /*#__PURE__*/React.createElement("ellipse", {
        cx: "17",
        cy: "31",
        rx: "2.3",
        ry: "1.9"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "14.6",
        cy: "28.2",
        r: "1.05"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "17",
        cy: "27.3",
        r: "1.05"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "19.4",
        cy: "28.2",
        r: "1.05"
      })));
    case 'smile':
      // no more worry
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("circle", {
        cx: "24",
        cy: "24",
        r: "15",
        fill: color,
        stroke: INK,
        strokeWidth: "2.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "18.5",
        cy: "21",
        r: "2",
        fill: INK
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "29.5",
        cy: "21",
        r: "2",
        fill: INK
      }), /*#__PURE__*/React.createElement("path", {
        d: "M16.5 27.5 C 20 32.8 28 32.8 31.5 27.5",
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinecap: "round",
        fill: "none"
      }));
    case 'flex':
      // cancel / change anytime
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("path", {
        d: "M34 16 A 12 12 0 1 0 36.5 25.5",
        stroke: INK,
        strokeWidth: "2.8",
        strokeLinecap: "round",
        fill: "none"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M27 8.5 L 35.5 15 L 28.5 20",
        fill: color,
        stroke: INK,
        strokeWidth: "2.5",
        strokeLinejoin: "round",
        strokeLinecap: "round"
      }));
    case 'chat':
      // the WhatsApp onboarding step
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("path", {
        d: "M24 9.5 C 33 9.5 40 15.3 40 23 C 40 30.7 33 36.5 24 36.5 C 21.7 36.5 19.5 36.1 17.6 35.4 L 9.8 38.3 L 12.5 31.4 C 9.7 29.1 8 26.2 8 23 C 8 15.3 15 9.5 24 9.5 Z",
        fill: CREAM,
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "17.5",
        cy: "23",
        r: "2.2",
        fill: color,
        stroke: INK,
        strokeWidth: "1.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "24",
        cy: "23",
        r: "2.2",
        fill: color,
        stroke: INK,
        strokeWidth: "1.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "30.5",
        cy: "23",
        r: "2.2",
        fill: color,
        stroke: INK,
        strokeWidth: "1.6"
      }));
    case 'calendar':
      // delivered every month
      return /*#__PURE__*/React.createElement("svg", common, /*#__PURE__*/React.createElement("rect", {
        x: "9",
        y: "13",
        width: "30",
        height: "27",
        rx: "4.5",
        fill: CREAM,
        stroke: INK,
        strokeWidth: "2.6"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M9 21 H 39 V 17 a4 4 0 0 0 -4 -4 H 13 a4 4 0 0 0 -4 4 Z",
        fill: color,
        stroke: INK,
        strokeWidth: "2.6",
        strokeLinejoin: "round"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M16 10 V 16 M32 10 V 16",
        stroke: INK,
        strokeWidth: "2.8",
        strokeLinecap: "round"
      }), /*#__PURE__*/React.createElement("g", {
        fill: color
      }, /*#__PURE__*/React.createElement("ellipse", {
        cx: "24",
        cy: "32",
        rx: "3.3",
        ry: "2.7"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "20.2",
        cy: "28.3",
        r: "1.45"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "23.4",
        cy: "26.9",
        r: "1.45"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "26.8",
        cy: "27.6",
        r: "1.45"
      })));
    default:
      return /*#__PURE__*/React.createElement(Icon, {
        name: name,
        size: size,
        color: color,
        style: style
      });
  }
}
window.DoodleIcon = DoodleIcon;
// ── Shared building blocks for the Boni homepage ──────────────────────────
// Layout/responsive classes live in boni-shopify-theme/assets/boni-home.css
// (bh-* prefix); components here carry only their own decorative styling.
const FLAV = ['teal', 'coral', 'leaf', 'berry', 'yellow', 'sand'];

// ── THE TWO NUMBERS/WORDS THE WHOLE SITE SHARES ───────────────────────────
// Shared.jsx is compiled BEFORE Sections/Sections2 (see scripts/build-web.sh),
// and the bundle is one concatenated script, so these are visible everywhere.
// Never re-declare either name in another .jsx file: a duplicate top-level
// const is a fatal SyntaxError that kills the WHOLE bundle (the S42 outage).
//
// MEMBERSHIP_PRICE was REMOVED 2026-10-25 (Maks + Ante launch list: no prices
// on the site). The beta is Journal-only and free for its whole length, with
// no automatic switch to paid. If a price ever comes back, reintroduce ONE
// constant here and read it everywhere, never a hardcoded number.

// ACCESS_LABEL: what we call the gate. "Rani pristup" over "zatvorena beta"
// (2026-09-22) for two reasons. (1) COMPREHENSION: *beta* is a software word
// and a chunk of the Croatian audience has never met it, while *rani pristup*
// is the standard Croatian rendering of early access and needs no decoding.
// (2) HONESTY OF THE PROMISE: *beta* implies unfinished AND free, so pairing
// it with a monthly price fights itself - which is exactly why the old copy
// had to carry "prvi mjesec besplatno" everywhere as a patch. Early access
// promises a privilege, not a test build, and a privilege can cost money.
// ONE constant: flipping it back to 'Zatvorena beta' is a one-line change.
const ACCESS_LABEL = 'Rani pristup';

// Photo — honest image slot. Shows the real photo once it loads; until then
// (or if missing) a flavour-tinted panel with the Boni paw + a tiny caption.
// Below-the-fold photos lazy-load by default; pass eager for above-the-fold.
function Photo({
  src,
  alt = '',
  tone = 'teal',
  radius = 'var(--radius-lg)',
  ratio,
  caption,
  eager = false,
  style = {}
}) {
  const [ok, setOk] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      width: '100%',
      aspectRatio: ratio,
      borderRadius: radius,
      overflow: 'hidden',
      background: `var(--${tone}-100)`,
      ...style
    }
  }, !ok && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      textAlign: 'center',
      padding: 18
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 58,
    color: `var(--${tone}-400)`,
    opacity: 0.5
  }), caption && /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 12.5,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      color: `var(--${tone}-600)`,
      opacity: 0.8,
      maxWidth: '85%'
    }
  }, caption)), src && !failed && /*#__PURE__*/React.createElement("img", {
    src: src,
    alt: alt,
    loading: eager ? 'eager' : 'lazy',
    decoding: "async",
    onLoad: () => setOk(true),
    onError: () => setFailed(true),
    style: {
      position: 'relative',
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      opacity: ok ? 1 : 0,
      transition: 'opacity var(--dur-slow) var(--ease-out)'
    }
  }));
}
window.Photo = Photo;

// Reveal — soft rise-in on scroll. Wrap INNER content blocks (a heading, a card
// grid), never a whole section: the transform creates a stacking context that
// would trap absolutely-positioned decorations/waves while animating.
// ?noanim in the URL (used by the screenshot harness) and prefers-reduced-motion
// both skip the animation synchronously.
function Reveal({
  children,
  delay = 0,
  className = '',
  style = {}
}) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No scroll-reveal on touch widths: it reads as lag rather than polish once
    // momentum scrolling outruns the observer. Matches the CSS above 899px.
    const skip = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.matchMedia && window.matchMedia('(max-width: 899px)').matches || /[?&]noanim/.test(window.location.search) || typeof IntersectionObserver === 'undefined';
    if (skip) {
      el.classList.add('bh-in');
      return;
    }
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        el.classList.add('bh-in');
        io.disconnect();
      }
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: 'bh-reveal' + (className ? ' ' + className : ''),
    style: {
      transitionDelay: delay ? delay + 'ms' : undefined,
      ...style
    }
  }, children);
}
window.Reveal = Reveal;

// Squiggle — a hand-drawn underline stroke for emphasising a headline word.
function Squiggle({
  color = 'var(--ink-800)',
  width = 220,
  height = 16,
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 220 16",
    width: width,
    height: height,
    fill: "none",
    style: {
      display: 'block',
      ...style
    },
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 11 C 45 3, 70 3, 110 9 S 180 14, 217 5",
    stroke: color,
    strokeWidth: "5",
    strokeLinecap: "round"
  }));
}
window.Squiggle = Squiggle;

// Wave — an organic divider rendered IN NORMAL FLOW as the last child of a
// section (class bh-wave reserves its own height), filled with the NEXT
// section's colour so the next band "rises up". Being in flow means it can
// never overpaint content — the old absolute/z-index version did.
function Wave({
  fill,
  variant = 0
}) {
  const d = ['M0,78 C220,140 430,150 720,104 C1010,60 1230,128 1440,84 L1440,160 L0,160 Z', 'M0,104 C260,44 520,150 800,98 C1040,54 1280,142 1440,92 L1440,160 L0,160 Z', 'M0,88 C300,150 540,48 820,96 C1080,138 1300,58 1440,108 L1440,160 L0,160 Z', 'M0,116 C240,52 500,142 760,94 C1040,42 1260,132 1440,80 L1440,160 L0,160 Z'][(variant % 4 + 4) % 4];
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-wave",
    "aria-hidden": "true",
    style: {
      marginBottom: -1
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 1440 160",
    preserveAspectRatio: "none",
    width: "100%",
    height: "100%",
    style: {
      display: 'block'
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: d,
    fill: fill
  })));
}
window.Wave = Wave;

// PawMark — a crisp vector paw doodle (recolourable), for watermarks & winks.
function PawMark({
  size = 40,
  rotate = 0,
  opacity = 0.5,
  color = 'var(--ink-400)',
  className = '',
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 100 100",
    width: size,
    height: size,
    fill: color,
    "aria-hidden": "true",
    className: 'bh-orn' + (className ? ' ' + className : ''),
    style: {
      transform: `rotate(${rotate}deg)`,
      opacity,
      ...style
    }
  }, /*#__PURE__*/React.createElement("ellipse", {
    cx: "50",
    cy: "70",
    rx: "27",
    ry: "20"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "20",
    cy: "38",
    rx: "9",
    ry: "10"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "40",
    cy: "24",
    rx: "9",
    ry: "12"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "60",
    cy: "24",
    rx: "9",
    ry: "12"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "80",
    cy: "38",
    rx: "9",
    ry: "10"
  }));
}
window.PawMark = PawMark;

// Pushpin — a thumbtack doodle for "pinning" polaroid-style cards.
function Pushpin({
  size = 26,
  color = 'var(--coral-400)',
  rotate = 0,
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 28 40",
    width: size,
    height: size * 40 / 28,
    fill: "none",
    "aria-hidden": "true",
    style: {
      display: 'block',
      transform: `rotate(${rotate}deg)`,
      filter: 'drop-shadow(1px 2px 0 rgba(15,53,87,0.22))',
      ...style
    }
  }, /*#__PURE__*/React.createElement("line", {
    x1: "14",
    y1: "15",
    x2: "14",
    y2: "38",
    stroke: "var(--ink-800)",
    strokeWidth: "2.6",
    strokeLinecap: "round"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "14",
    cy: "12",
    rx: "11",
    ry: "10",
    fill: color,
    stroke: "var(--ink-800)",
    strokeWidth: "2.6"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "10",
    cy: "8.5",
    rx: "3.1",
    ry: "2.3",
    fill: "var(--white)",
    opacity: "0.6"
  }));
}
window.Pushpin = Pushpin;

// MotionLines — three little speed strokes (the running-dog "whoosh" doodle).
function MotionLines({
  size = 38,
  color = 'var(--ink-300)',
  rotate = 0,
  className = '',
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 40 40",
    width: size,
    height: size,
    fill: "none",
    stroke: color,
    strokeWidth: "3",
    strokeLinecap: "round",
    "aria-hidden": "true",
    className: 'bh-orn' + (className ? ' ' + className : ''),
    style: {
      transform: `rotate(${rotate}deg)`,
      ...style
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4 11 C 14 8, 23 8, 33 12"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M2 21 C 13 18, 24 18, 35 22"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7 31 C 15 28, 23 28, 31 31"
  }));
}
window.MotionLines = MotionLines;

// Sticker badge — small chip used for floating accents.
function Chip({
  children,
  bg = 'var(--surface-raised)',
  color = 'var(--ink-800)',
  rotate = 0,
  style = {}
}) {
  return /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      background: bg,
      color,
      border: '1px solid var(--hair)',
      borderRadius: 'var(--radius-pill)',
      boxShadow: 'var(--shadow-sticker-sm)',
      padding: '9px 15px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 13.5,
      transform: `rotate(${rotate}deg)`,
      whiteSpace: 'nowrap',
      ...style
    }
  }, children);
}
window.Chip = Chip;

// Dog-food bowl — the cart icon (Boni sells food, so the basket is a bowl).
function BowlIcon({
  size = 20,
  color = 'currentColor'
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    fill: "none",
    stroke: color,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3.5 10.5c0 4.5 2 8 8.5 8s8.5-3.5 8.5-8"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "12",
    cy: "10.2",
    rx: "8.5",
    ry: "3.1"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "9.5",
    cy: "9.8",
    r: "0.9",
    fill: color,
    stroke: "none"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "13",
    cy: "10.6",
    r: "0.9",
    fill: color,
    stroke: "none"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "14.6",
    cy: "9.4",
    r: "0.9",
    fill: color,
    stroke: "none"
  }));
}
window.BowlIcon = BowlIcon;

// Sparkle — 4-point doodle for playful accents.
function Sparkle({
  size = 22,
  color = 'var(--teal-400)',
  className = '',
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    className: 'bh-orn' + (className ? ' ' + className : ''),
    style: {
      ...style
    },
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 0c1 7 4 11 12 12-8 1-11 5-12 12-1-7-4-11-12-12 8-1 11-5 12-12Z",
    fill: color
  }));
}
window.Sparkle = Sparkle;

// Crown — hand-drawn 3-point crown doodle.
function Crown({
  size = 46,
  color = 'var(--yellow-500)',
  className = '',
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 48 34",
    width: size,
    height: size * 34 / 48,
    fill: "none",
    stroke: color,
    strokeWidth: "4",
    strokeLinejoin: "round",
    strokeLinecap: "round",
    className: 'bh-orn' + (className ? ' ' + className : ''),
    style: {
      ...style
    },
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 29 L3 9 L16 19 L24 5 L32 19 L45 9 L43 29 Z",
    fill: color,
    fillOpacity: "0.18"
  }));
}
window.Crown = Crown;

// PawPrint — an ink paw, for the step trail & accents.
function PawPrint({
  size = 26,
  color = 'var(--ink-300)',
  rotate = 0,
  className = '',
  style = {}
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 32 32",
    width: size,
    height: size,
    fill: color,
    className: 'bh-orn' + (className ? ' ' + className : ''),
    style: {
      transform: `rotate(${rotate}deg)`,
      ...style
    },
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("ellipse", {
    cx: "16",
    cy: "22",
    rx: "7.5",
    ry: "6.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "7",
    cy: "13",
    r: "3.2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "13.5",
    cy: "8.5",
    r: "3.2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "18.5",
    cy: "8.5",
    r: "3.2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "25",
    cy: "13",
    r: "3.2"
  }));
}
window.PawPrint = PawPrint;

// WaProfileCard — Boni's live WhatsApp Business profile, recreated at card
// size: iOS grouped background, white rows, hairline dividers, the REAL cream
// avatar. Shared by the WhatsApp page (section 2) and the homepage bridge, so
// she is introduced as the same OBJECT everywhere and the reader recognises
// the screen when they get there. Dropped from the real screen on purpose:
// Share/Search (dead chrome), the boni.hr link (the reader is on it) and
// WhatsApp's English "Education" label (reads as a bug on a Croatian page).
// `desc` = the description row; default is the live profile text.
function WaProfileCard({
  desc = '🐶 Ako želiš najbolje za svog psa · 📦 Hrana koja stiže kad treba · 💙 Boni Club'
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-profile"
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-chat-avatar-v3.png',
    alt: "Boni",
    loading: "lazy",
    decoding: "async",
    className: "bh-wap-av"
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-name"
  }, "Boni"), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-num"
  }, "+385 97 612 8956"), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-row"
  }, "Manje brige. Više zajedničkih trenutaka."), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-row bh-wap-pf-row--sub"
  }, desc)), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-pf-card bh-wap-pf-biz"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "bh-wap-pf-i",
    viewBox: "0 0 24 24",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "10.2",
    fill: "none",
    stroke: "#667781",
    strokeWidth: "1.7"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "7.4",
    r: "1.25",
    fill: "#667781"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12 10.8v6.2",
    stroke: "#667781",
    strokeWidth: "1.9",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Business Account"), /*#__PURE__*/React.createElement("span", null, "Ovaj račun koristi WhatsApp Business"))));
}
window.WaProfileCard = WaProfileCard;
// ── Top of the Boni homepage ──────────────────────────────────────────────
// Layout/responsive rules live in boni-shopify-theme/assets/boni-home.css
// (bh-* classes). Copy is LOCKED (S81/S82 spine) — restyle freely, never
// reword. Header/Announce/Footer are shared chrome mounted on every page.
const DARK_BTN = {
  background: 'var(--brand)',
  color: 'var(--white)',
  border: 'none'
};

// Reveals the Mindsmiths subscription wizard. The widget (loaded in theme.liquid with
// startStrategy: 'load') sits tucked at the bottom; opening = setting its ui.open state true.
// If for any reason it isn't on the page yet, we create it and open once its state is ready.
function openBoniWizard() {
  var openIt = function (el) {
    if (el && el.setState && el.state && el.state.ui) {
      el.setState({
        ui: Object.assign({}, el.state.ui, {
          open: true
        })
      });
      return true;
    }
    return false;
  };
  var w = document.querySelector('mindsmiths-widget');
  if (w && openIt(w)) return;
  if (!w) {
    w = document.createElement('mindsmiths-widget');
    window.MindsmithsWidget = w;
    document.body.appendChild(w);
  }
  var tries = 0;
  var t = setInterval(function () {
    if (openIt(w) || ++tries > 40) clearInterval(t);
  }, 150);
}
window.openBoniWizard = openBoniWizard;
const WRAP = {
  maxWidth: 'var(--container)',
  margin: '0 auto',
  padding: '0 var(--gutter)'
};

// Sits BELOW the sticky header (App order: Header → Announce). Gold = the
// reward band in the system. Rotates between what the membership contains and
// the one perk that is a physical object.
// The old pair is GONE (2026-09-12): "Boni Club je besplatan uz plan dostave"
// became false the moment membership became paid, and the "20% + poklon" line
// promised a welcome gift that does not exist.
// Grid-stacked lines: the tallest wrapped line sets the height, nothing spills.
function Announce() {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI(x => (x + 1) % 2), 6000);
    return () => clearInterval(t);
  }, []);
  const link = {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer'
  };
  const line = on => ({
    opacity: on ? 1 : 0,
    pointerEvents: on ? 'auto' : 'none'
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-announce",
    onClick: openBoniWizard,
    style: {
      cursor: 'pointer',
      background: 'var(--yellow-400)',
      color: 'var(--ink-900)',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 14,
      letterSpacing: '.01em',
      borderBottom: '1px solid var(--hair)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-announce-line",
    style: line(i === 0)
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 15,
    opacity: 1,
    color: "var(--ink-900)",
    style: {
      flex: 'none'
    }
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, ACCESS_LABEL, "."), " Mjesta otvaramo polako")), /*#__PURE__*/React.createElement("div", {
    className: "bh-announce-line",
    style: line(i === 1)
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 15,
    opacity: 1,
    color: "var(--ink-900)",
    style: {
      flex: 'none'
    }
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, "Prijave"), " čitamo jednu po jednu")));
}
window.Announce = Announce;

// `minimal` strips the shop furniture (cart icon + the WhatsApp nav item) so the
// homepage reads as a membership page rather than a webshop. It is passed ONLY by
// App.jsx; theme.liquid mounts this component with no props, so every shop page
// keeps the full header. Never make the minimal version the default.
function Header({
  minimal = false
}) {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  const [open, setOpen] = React.useState(false);
  const hdrRef = React.useRef(null);

  // ── HIDE ON SCROLL DOWN, REAPPEAR ON SCROLL UP (the Edgard & Cooper pattern,
  // user 2026-09-23). The header stays put through the first section, then gets
  // out of the way while you read, and comes straight back the moment you
  // scroll up — so the CTA is never more than a flick away without the bar
  // eating 64px of every screen.
  //
  // THREE THINGS THAT KEEP IT FROM FEELING BROKEN:
  //  1. It NEVER hides inside the first section. Measured from the real first
  //     section rather than a magic number, so it adapts to any page, and
  //     re-measured on resize because the hero's height is viewport-derived.
  //  2. A 6px dead zone. Without it, sub-pixel scroll jitter and trackpad
  //     momentum flip the bar on and off continuously.
  //  3. Reduced motion drops the transition (the class still toggles, so the
  //     behaviour survives, it just does not slide).
  // Reads are batched into rAF; the listener is passive so it never blocks
  // scrolling. Everything is a class toggle, so no React re-render per frame.
  React.useEffect(() => {
    const el = hdrRef.current;
    if (!el) return;
    let threshold = 0;
    const measure = () => {
      const first = document.querySelector('.bh-section, main section, section');
      const h = first ? first.getBoundingClientRect().height : 0;
      // fall back to roughly one screen when a page has no section markup
      threshold = Math.max(120, h || window.innerHeight * 0.8);
    };
    measure();
    let last = window.scrollY,
      ticking = false;
    const update = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY); // iOS rubber-band guard
      if (y <= threshold) {
        el.classList.remove('bh-header--hidden');
        last = y;
        return;
      }
      const dy = y - last;
      if (Math.abs(dy) < 6) return; // dead zone
      el.classList.toggle('bh-header--hidden', dy > 0);
      last = y;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
    };
  }, []);
  const link = {
    fontFamily: 'var(--font-text)',
    fontWeight: 700,
    fontSize: 15.5,
    color: 'var(--ink-800)',
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  };
  // display comes from .bh-icon-btn in CSS so media queries can hide buttons
  const iconBtn = {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
    borderRadius: '50%',
    background: 'var(--cream-200)',
    color: 'var(--ink-800)',
    cursor: 'pointer',
    border: '1px solid var(--hair)',
    flex: 'none'
  };
  // "Boni Family" is treated as INVARIABLE in Croatian — it declines badly
  // (u Boni Familyju / dio Boni Familyja), so every use must sit in a
  // nominative slot, the same discipline as the dog-name rule.
  // Boni Family FIRST: the membership is what we sell, the shop is the second
  // door. "Shop" rather than "Hrana" because it names the place, not today's
  // contents — food is all that is in there now, but the brand core says more
  // is coming, and a category label would date.
  // MINIMAL = THE HOMEPAGE, AND IT IS NOW A LANDING PAGE WITH NO NAV AT ALL
  // (user, 2026-09-23). While we are only taking early-access applications this
  // is the single page that matters, so every nav link is a way to leave it
  // before applying. The logo and the one CTA are all that stay.
  // ⚠ `minimal` is passed ONLY by App.jsx, which is homepage-only by
  // construction. theme.liquid mounts this same Header with NO props on every
  // other page, so the shop keeps its full nav - do not move this logic out of
  // the prop (S103).
  const NAV = minimal ? [] : [['Boni Family', '/'], ['Shop', '/collections/sva-hrana'], ['WhatsApp', '/pages/whatsapp']];
  return /*#__PURE__*/React.createElement("header", {
    ref: hdrRef,
    className: 'bh-header' + (minimal ? ' bh-header--bare' : ''),
    style: {
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: 'var(--white)',
      borderBottom: '1px solid var(--hair)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-header-row"
  }, /*#__PURE__*/React.createElement("a", {
    href: "/",
    style: {
      display: 'flex',
      alignItems: 'center',
      flex: 'none'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-logo-violet.png',
    alt: "Boni",
    className: "bh-logo"
  })), /*#__PURE__*/React.createElement("nav", {
    className: "bh-nav"
  }, NAV.map(([label, href]) => /*#__PURE__*/React.createElement("a", {
    key: label,
    style: link,
    href: href
  }, label))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: 10
    }
  }, !minimal && /*#__PURE__*/React.createElement("button", {
    className: "bh-icon-btn bh-profile-btn",
    style: iconBtn,
    "aria-label": "Profil",
    onClick: () => {
      window.location.href = '/account';
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "user",
    size: 18
  })), !minimal && /*#__PURE__*/React.createElement("button", {
    className: "bh-icon-btn",
    style: {
      ...iconBtn,
      position: 'relative'
    },
    "aria-label": "Košarica",
    onClick: () => {
      window.location.href = '/cart';
    }
  }, /*#__PURE__*/React.createElement(BowlIcon, {
    size: 20
  }), /*#__PURE__*/React.createElement("span", {
    className: "bh-cart-badge",
    "data-cart-count": true,
    hidden: true
  })), /*#__PURE__*/React.createElement("span", {
    className: "bh-header-cta"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: DARK_BTN,
    onClick: openBoniWizard
  }, "Prijavi se")), !minimal && /*#__PURE__*/React.createElement("button", {
    className: "bh-burger",
    "aria-label": "Izbornik",
    "aria-expanded": open,
    onClick: () => setOpen(!open)
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: "21",
    height: "21",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    "aria-hidden": "true"
  }, open ? /*#__PURE__*/React.createElement("path", {
    d: "M6 6l12 12M18 6L6 18"
  }) : /*#__PURE__*/React.createElement("path", {
    d: "M4 7h16M4 12h16M4 17h16"
  }))))), open && !minimal && /*#__PURE__*/React.createElement("div", {
    className: "bh-drawer"
  }, NAV.map(([label, href]) => /*#__PURE__*/React.createElement("a", {
    key: label,
    className: "bh-drawer-link",
    href: href,
    onClick: () => setOpen(false)
  }, label)), /*#__PURE__*/React.createElement("a", {
    className: "bh-drawer-link",
    href: "/account",
    onClick: () => setOpen(false)
  }, "Moj račun")));
}
window.Header = Header;

// 1 · HERO — the promise + the category, on a clean WHITE base. Two cut-out
// dogs on soft palette blobs; their bottoms TUCK UNDER the next section's
// wave (blush fill, z-index above the dogs), which is what makes them read
// as photos emerging from behind the page rather than clipped cut-outs.
function Hero() {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-hero bh-section--wave",
    style: {
      background: 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--coral-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 118,
      left: '26%'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-hero-inner",
    style: {
      padding: '0 var(--gutter)'
    }
  }, /*#__PURE__*/React.createElement("h1", {
    style: {
      fontFamily: 'var(--font-display)',
      fontWeight: 800,
      fontSize: 'clamp(2rem, 4.2vw, 3.3rem)',
      lineHeight: 1.1,
      letterSpacing: '-.02em',
      margin: 0,
      color: 'var(--ink-900)'
    }
  }, "Manje brige.", /*#__PURE__*/React.createElement("br", null), "Više zajedničkih", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'relative',
      display: 'inline-block'
    }
  }, "trenutaka", /*#__PURE__*/React.createElement(Squiggle, {
    color: "var(--coral-400)",
    width: "100%",
    height: 16,
    style: {
      position: 'absolute',
      left: 0,
      bottom: -8,
      width: '100%'
    }
  }))), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: 'var(--font-text)',
      fontSize: 'clamp(1.15rem, 2.2vw, 1.55rem)',
      fontWeight: 700,
      lineHeight: 1.45,
      color: 'var(--ink-800)',
      maxWidth: 680,
      margin: '16px auto 0'
    }
  }, "Prvo mjesto za pet parente u Hrvatskoj."), /*#__PURE__*/React.createElement("div", {
    className: "bh-cta-row"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "lg",
    style: DARK_BTN,
    onClick: openBoniWizard
  }, "Prijavi se")), /*#__PURE__*/React.createElement("p", {
    className: "bh-hero-cap"
  }, "Rani pristup je u tijeku. Mjesta otvaramo polako.")), /*#__PURE__*/React.createElement("div", {
    className: "bh-hero-dogs"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-hero-dog bh-hero-dog--left"
  }, /*#__PURE__*/React.createElement(HeroDog, {
    src: window.K + 'hero-dog-left-v11.webp',
    caption: "Labradoodle"
  }), /*#__PURE__*/React.createElement("span", {
    className: "bh-hero-bubble",
    style: {
      position: 'absolute',
      top: '44%',
      right: '-13%',
      zIndex: 3,
      background: 'var(--surface-raised)',
      border: '1px solid var(--hair)',
      borderRadius: '16px 16px 16px 4px',
      boxShadow: 'var(--shadow-sticker-sm)',
      padding: '7px 13px',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 14.5,
      color: 'var(--coral-400)',
      transform: 'rotate(-4deg)',
      whiteSpace: 'nowrap'
    }
  }, "Napokon!")), /*#__PURE__*/React.createElement("div", {
    className: "bh-hero-dog bh-hero-dog--right"
  }, /*#__PURE__*/React.createElement(HeroDog, {
    src: window.K + 'hero-dog-right-v15.webp',
    caption: "Cavalier King Charles"
  }), /*#__PURE__*/React.createElement(Crown, {
    className: "bh-hero-crown",
    color: "var(--yellow-400)",
    style: {
      position: 'absolute',
      top: '14%',
      left: '34%',
      transform: 'translateX(-50%) rotate(-10deg)',
      zIndex: 3
    }
  }))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-900)",
    variant: 2
  }));
}
window.Hero = Hero;
function HeroDog({
  src,
  caption,
  aspect = '580 / 865'
}) {
  const [ok, setOk] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const show = src && !failed;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      aspectRatio: aspect,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center'
    }
  }, show && /*#__PURE__*/React.createElement("img", {
    src: src,
    alt: caption,
    fetchpriority: "high",
    onLoad: () => setOk(true),
    onError: () => setFailed(true),
    style: {
      position: 'relative',
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }
  }), !ok && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 46,
    opacity: 0.7
  }), caption && /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 11.5,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      color: 'var(--ink-800)',
      opacity: 0.72,
      textAlign: 'center',
      maxWidth: '78%'
    }
  }, caption))));
}
const BRANDS = [{
  name: 'Royal Canin',
  slug: 'royal-canin',
  tag: 'Veterinarski',
  tone: 'teal',
  desc: '50+ godina istraživanja. Formula po pasmini, dobi i veličini.'
}, {
  name: "Hill's Science Plan",
  slug: 'hills',
  tag: 'Klinički',
  tone: 'teal',
  desc: 'Razvijen uz 220+ veterinara. Precizan i provjeren.'
}, {
  name: 'Orijen',
  slug: 'orijen',
  tag: 'Bez žitarica',
  tone: 'leaf',
  desc: '85% svježeg mesa, peradi ili ribe. Bez punila.'
}, {
  name: 'Acana',
  slug: 'acana',
  tag: 'Sa farme',
  tone: 'coral',
  desc: 'Sastojci s farme i ribarnice. Bez konzervansa.'
}, {
  name: 'Brit Premium',
  slug: 'brit',
  tag: 'Povoljno',
  tone: 'berry',
  desc: 'Europska kvaliteta po pristupačnoj cijeni.'
}, {
  name: 'Arquivet',
  slug: 'arquivet',
  tag: 'Novo',
  tone: 'yellow',
  desc: 'Španjolski brend, ukusan obrok svaki dan.'
}, {
  name: 'K9',
  slug: 'k9',
  tag: 'Aktivni psi',
  tone: 'coral',
  desc: 'Za pse koji se kreću, igraju i žive punim plućima.'
}, {
  name: 'Purina Pro Plan',
  slug: 'purina',
  tag: 'Profi izbor',
  tone: 'teal',
  desc: 'Iza svake formule 400+ istraživača i veterinara.'
}];
const BRAND_BG = {
  'royal-canin': '#f6f1e7',
  hills: '#053b53',
  orijen: '#fefefe',
  acana: '#f5edd6',
  brit: '#15489d',
  arquivet: '#fafaf5',
  k9: '#f7f4e8',
  purina: '#ffffff'
};

// 4 · BRANDS — two columns on the cool navy wash: copy (title + trust point +
// CTA) left, the big cycling brand showcase right. Auto-cycles; arrows + dots
// for manual browsing. Uses the same banner scenes as the brand pages.
function Brands() {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  // A WALL, not a carousel. The job of this section is an availability test:
  // a visitor scans for the brand they already buy and needs to find it in a
  // second. One cycling banner did the opposite — it put a single brand in the
  // foreground and made the visitor wait 8s per brand to learn whether theirs
  // was carried. The per-brand banners still exist; their home is the brand
  // collection page, where one brand SHOULD be in the foreground.
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--ink-50)'
    }
  }, /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--coral-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 64,
      left: '4%'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      maxWidth: 760,
      margin: '0 auto 14px',
      textAlign: 'center'
    }
  }, "Tvoj pas dobiva samo hranu najboljih brendova."), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      marginBottom: 'var(--space-7)'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 'none',
      width: 24,
      height: 24,
      borderRadius: '50%',
      background: 'var(--leaf-600)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: "14",
    height: "14",
    fill: "none",
    stroke: "var(--white)",
    strokeWidth: "3.4",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4.5 12.5l5 5 10-11"
  }))), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-text)',
      fontWeight: 700,
      fontSize: 16,
      lineHeight: 1.5,
      color: 'var(--ink-700)'
    }
  }, "Surađujemo samo s brendovima kojima ljudi vjeruju godinama."))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 70
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-brand-wall"
  }, BRANDS.map(br => /*#__PURE__*/React.createElement("a", {
    key: br.slug,
    href: '/collections/' + br.slug,
    className: "bh-brand-tile",
    "aria-label": br.name
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-brand-logo",
    style: {
      background: BRAND_BG[br.slug] || 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + `brands-${br.slug}-logo-v2.jpeg`,
    alt: br.name,
    loading: "lazy",
    decoding: "async"
  })), /*#__PURE__*/React.createElement("span", {
    className: "bh-brand-tag"
  }, br.tag))))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 120
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: 'var(--space-7)'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "lg",
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: DARK_BTN,
    onClick: () => {
      window.location.href = '/collections/sva-hrana';
    }
  }, "Pogledaj svu hranu")))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--teal-100)",
    variant: 0
  }));
}
window.Brands = Brands;
// ── Lower half of the Boni homepage ───────────────────────────────────────
// Section order (App.jsx): Hero → Villain → Journal → FoodPlan → GoodDeed →
// JoinClose → Footer. The headings read top-to-bottom as one story — the spine
// is in boni_brand_core.md §2. Rebuilt 2026-09-12 for the membership
// repositioning (was: Mission → BoniCare → HowItWorks → Brands → GoodDeed →
// Testimonials → JoinClose).
//
// HOW WE WRITE HERE: every block is MESSAGE + CONTEXT. The message is one plain
// statement in the reader's terms; the context is short and factual. No poetry,
// no aphorisms, no rhythm for its own sake — that belongs in the manifesto,
// which is an internal north star, not page copy.
//
// Layout/responsive rules live in boni-home.css (bh-* classes). Nothing here
// needed a new class: Journal reuses the BoniCare phone grid, FoodPlan reuses
// the HowItWorks card grid.
// Colour roles: navy = the brand · mint = the good · pink = emotion · gold =
// reward, sparing. Background arc:
// WHITE → NAVY → BLUSH → white → MINT → white (navy panel) → NAVY footer.

// 2 · VILLAIN — the emotional low point, on the navy band. One h2, one
// bullet list of lived moments, one coral turn (rebuilt 2026-09-16; before
// that a single h2 + coral line, and before that five blocks). Cut from five blocks to one on 2026-09-12 (user: "way too much
// text, get it to just 1 text box like it was before").
//
// COPY DECISIONS, do not "improve" them away:
// 1. The villain is GENERAL and says "ne shvaćaju" OUT LOUD; only the turn
//    points at the reader. Opening in second person reads as an accusation;
//    a general observation followed by "nama ne moraš objašnjavati" reads as
//    the answer to it. An earlier draft that only implied the villain ("vide
//    psa samo kao životinju") made the turn sound like us telling everyone
//    how to treat their dog.
// 2. "Nama ne moraš objašnjavati" ENACTS the acceptance ask instead of
//    stating it ("želimo da to postane normalno" was cut for exactly this).
//    The ask is LGBTQ-shaped: you do not have to be like us, just accept that
//    we are. Never "normalizirati" — the one word that sounds like a deck.
// 3. The close (JoinClose) still ends on "Ovdje ne moraš objašnjavati". That
//    is a deliberate REFRAIN, not a repeat: here it is the people (nama),
//    there it is the place (ovdje). Do not add a third.
// 4. "Nama" = we, the Boni team. Honest: there is no feed, no profiles, no
//    group, so "drugi pet parenti razumiju" would be a lie; "we understand"
//    is not.
// The photo is people like us, not us: a scruffy weekend dog-walk meetup in
// Maksimir-style park, mismatched jackets, nobody looking at the camera,
// spring light and spring clothes (v1 overcast, v2 spring sky but still in jackets - both fixed via --input-image edits, user). The
// earlier team portrait in matching Boni shirts read "too perfect, too
// cinematic". The chip on the photo reads "Boni Family" and the h2 does NOT
// end in "Zato gradimo Boni Family" (user's call, 2026-09-12): the name moved
// off the sentence and onto the picture, so the photo is what Boni Family IS
// and the text stays two clean beats (villain, turn). A "Pet parenti" chip was
// tried and cut because it only restated what the image shows.
// HONESTY DEBT, taken knowingly: the photo is generated, no Boni gathering has
// ever happened, and a "Boni Family" chip on it reads as one. Swap for a real
// šetnja photo the moment one exists (new filename!).
// VILLAIN DOODLES (2026-09-22) - four small, deliberately imperfect line
// illustrations beside the four thoughts: a paw, a leash, a photo and a bowl.
// Hand-written rather than fetched so they never fail to paint and so the
// stroke can stay thin on the navy band.
// COLOUR RULE: the outline is lavender (--sky-200) and only ONE detail per
// doodle is coral. Coral is the section's payoff colour ("Nama ne moras
// objasnjavati"), so it appears here as an accent and never as a statement.
function VilDoodle({
  name,
  size = 60
}) {
  const LINE = 'var(--sky-200)';
  const POP = 'var(--coral-400)';
  const c = {
    width: size,
    height: size,
    viewBox: '0 0 40 40',
    fill: 'none',
    stroke: LINE,
    strokeWidth: 2.2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    style: {
      display: 'block',
      overflow: 'visible'
    }
  };
  switch (name) {
    case 'paw':
      // brines se je li dobro
      return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
        d: "M20 33.4c-5.2 0-8.7-2.5-8.7-5.9 0-3.3 3.6-5.7 8.7-5.7s8.7 2.4 8.7 5.7c0 3.4-3.5 5.9-8.7 5.9Z"
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "9.7",
        cy: "18",
        rx: "3.1",
        ry: "3.9",
        transform: "rotate(-18 9.7 18)"
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "16.5",
        cy: "12.2",
        rx: "3",
        ry: "3.9",
        transform: "rotate(-7 16.5 12.2)"
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "23.9",
        cy: "12.4",
        rx: "3",
        ry: "3.9",
        transform: "rotate(8 23.9 12.4)",
        fill: POP,
        stroke: POP
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "30.5",
        cy: "18.3",
        rx: "3.1",
        ry: "3.9",
        transform: "rotate(19 30.5 18.3)"
      }));
    case 'leash':
      // ne volis ga ostaviti samog
      // The hardest of the four to read small, and THREE versions came out as a
      // MAGNIFYING GLASS. The cause is structural, not a matter of curvature: a
      // ring with a line leaving its edge IS a lens with a handle, however
      // slack the line. What a lens can never do is CROSS ITSELF, so here the
      // lead starts at the loop's lower-left and passes back over the loop on
      // its way down. That one overlap is what makes it read as rope, and the
      // slight sag plus the open clip ring at the end finish it.
      return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("ellipse", {
        cx: "14.6",
        cy: "11.8",
        rx: "6.2",
        ry: "5.2",
        transform: "rotate(-16 14.6 11.8)"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M10.4 15.2c1.4 3.6 6.6 4.4 9.4 7.2 2.4 2.4 3.2 5 2.2 7.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "21.2",
        cy: "32.8",
        r: "3",
        stroke: POP
      }));
    case 'photo':
      // tisucu njegovih fotografija
      return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("rect", {
        x: "6.6",
        y: "5.8",
        width: "26.8",
        height: "28.4",
        rx: "3",
        transform: "rotate(-3 20 20)"
      }), /*#__PURE__*/React.createElement("rect", {
        x: "9.9",
        y: "9.2",
        width: "20.2",
        height: "15.4",
        rx: "2",
        transform: "rotate(-3 20 20)"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M13.2 29.9h9.6",
        transform: "rotate(-3 20 20)"
      }), /*#__PURE__*/React.createElement("g", {
        transform: "rotate(-3 20 20)",
        fill: POP,
        stroke: "none"
      }, /*#__PURE__*/React.createElement("ellipse", {
        cx: "20",
        cy: "19.6",
        rx: "3.3",
        ry: "2.6"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "15.9",
        cy: "15.6",
        r: "1.3"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "18.8",
        cy: "14.1",
        r: "1.3"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "22.1",
        cy: "14.6",
        r: "1.3"
      })));
    case 'bowl': // pazis sto jede
    default:
      return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
        d: "M7.2 18.6c.8 8.5 5.9 13.3 12.8 13.3s12-4.8 12.8-13.3"
      }), /*#__PURE__*/React.createElement("ellipse", {
        cx: "20",
        cy: "18.2",
        rx: "12.8",
        ry: "4.2"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "15.6",
        cy: "13.8",
        r: "1.8"
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "20.6",
        cy: "12.3",
        r: "1.8",
        fill: POP,
        stroke: POP
      }), /*#__PURE__*/React.createElement("circle", {
        cx: "25.2",
        cy: "14.2",
        r: "1.8"
      }));
  }
}
function Villain() {
  // The four thoughts are the manifesto's own list (boni_brand_core.md, verbatim
  // passage: "Ne razumiju zašto ne može ostati sam cijeli dan... Zašto imaš
  // tisuću njegovih fotografija. I zašto ti je važno što jede."), so each is a
  // moment the reader has actually lived.
  // TONE GUARD: they name what YOU do, never what THEY say. Quoting a
  // dismissive line ("pa to je samo pas") would tip the section into
  // resentment, and the purpose is to normalise, never to be above anyone.
  // Two of the four plant later sections on purpose: photos -> Journal (01),
  // what he eats -> the food plan (02).
  // ROUND 5 (2026-09-22, user: the four cards "look too much like a generic
  // features section from an AI template" - and the diagnosis was precise: the
  // problem is not the messages, it is that everything is equally sized,
  // symmetric and explained by the same pattern). So: NO cards. Four personal
  // thoughts in a run, each shifted and tilted differently, each with one small
  // imperfect illustration, then the dismissal ONCE (small and dim) and the
  // answer ONCE (big and coral).
  // The per-line dismissals had to go: "Drugima je to X." four times over WAS
  // the repeated pattern that made the section read as a template.
  // ROUND 6 (2026-09-22): every line must be something a person who does NOT
  // live this would never say. "Brineš se je li dobro" failed that test -
  // everyone worries about their dog, so it proves nothing and reads as filler.
  // The replacements are all specific and slightly self-aware, which is what
  // makes them recognisable rather than generic.
  const moments = [['S njim pričaš kao da te razumije.', 'paw'], ['Ne možeš ga ostaviti samog cijeli dan.', 'leash'], ['Galerija ti je puna njegovih fotografija.', 'photo'], ['Hranu mu biraš pažljivije nego sebi.', 'bowl']];
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--ink-900)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 92,
    rotate: 16,
    opacity: 0.16,
    color: "var(--berry-300)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      bottom: 26,
      left: -24
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-vil2"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2 bh-vil-h2",
    style: {
      color: 'var(--white)'
    }
  }, "Biti pet parent nije uvijek lako. Pogotovo kada ljudi oko tebe to ne razumiju.")), /*#__PURE__*/React.createElement("div", {
    className: "bh-vil-run"
  }, moments.map(([thought, ico], i) => /*#__PURE__*/React.createElement(Reveal, {
    key: thought,
    delay: 70 * i
  }, /*#__PURE__*/React.createElement("div", {
    className: 'bh-vil-th bh-vil-th--' + (i + 1)
  }, /*#__PURE__*/React.createElement(VilDoodle, {
    name: ico
  }), /*#__PURE__*/React.createElement("p", null, thought)))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 300
  }, /*#__PURE__*/React.createElement("p", {
    className: "bh-vil-others"
  }, "Drugima je to možda pretjerivanje."), /*#__PURE__*/React.createElement("p", {
    className: "bh-vil-turn"
  }, "Nama ne moraš objašnjavati.")))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--coral-50)",
    variant: 1
  }));
}
window.Villain = Villain;

// 2b · HOW BRIDGE — REBUILT 2026-09-16 as the phone composition (the
// Too-Good-To-Go grid that Journal used to own). It does the three jobs the
// story kept skipping: (1) the HINGE — the social villain becomes the reason a
// product exists ("nobody built anything for it, so we did"); (2) it says Boni
// is an ASSISTANT in ONE CHAT, so "mjesto" on this page stops meaning a
// WhatsApp group; (3) it names the MEMBERSHIP once, plainly, so checkout is
// not a surprise. The four points are the BENEFITS only (the rail's own
// labels + where she lives); concrete features live in 01 · 02 · 03, so this
// reads as their table of contents rather than pre-empting them.
// VOICE RULE FOR THE WHOLE PAGE (2026-09-19, feedback: "the web is the menu,
// the wizard is the waiter"): every headline is a PLAIN STATEMENT that stands
// alone, readable out of order like a menu item, and the READER is the one
// doing the action ("Uključi plan dostave", "Sačuvaj svaki trenutak"). No
// "we noticed X, pa smo built Y" chain any more: the founder story lives in
// the wizard and boni_brand_core.md §2, the homepage shows what is on offer.
// The one narrative sentence left on the page is the bridge sub-line.
// The phone shows her FIRST message to a new member. That is the exact
// conversation the cold-arrival playbook must deliver, so nothing on screen is
// invented; "spreman" and any health invitation are kept out on purpose.
function HowBridge() {
  const points = [
  // ROUND 7 (2026-09-22, user): this section used to be a general
  // "meet Boni" band, which said nothing you could picture. It is now a
  // real capability: you ASK her things and the answer is about YOUR dog.
  // The unspoken comparison is a generic AI chat, and we never say it out
  // loud - we just show the difference, which is that she already knows him.
  // BETA (2026-10-25): beta Boni is a dog-talk + Journal persona, not an
  // advice desk, so "pitaj je za savjet ili preporuku" was cut.
  'Pričaš o svom psu kao s prijateljem, bez ispunjavanja ičega.', 'Pamti tvog psa, njegove navike i ljude oko njega.', 'Sve na WhatsAppu, bez aplikacije i bez registracije.',
  // NOT the health boundary (user 2026-09-22: it is a limit, not a feature,
  // and this section is a menu item). The persona still refuses symptom talk
  // and sends you to a vet - it simply is not advertised here.
  'Odgovori ti odmah, u bilo koje doba dana.'];
  return /*#__PURE__*/React.createElement("section", {
    id: "boni",
    className: "bh-section bh-section--wave bh-bridge",
    style: {
      background: 'var(--ink-50)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 96,
    rotate: -14,
    opacity: 0.2,
    color: "var(--ink-200)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 44,
      right: -22
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap",
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement(PillarTag, {
    n: "02",
    label: "Boni"
  }), /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      color: 'var(--ink-900)',
      maxWidth: 820,
      margin: '0 auto',
      textAlign: 'center'
    }
  }, "Pričaj o svom psu.", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "Boni pamti svaki detalj."))), /*#__PURE__*/React.createElement("div", {
    className: "bh-care-tgtg"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "bh-care-side bh-care-side--left"
  }, [0, 2].map((n, i) => /*#__PURE__*/React.createElement(CareCheck, {
    key: points[n],
    text: points[n],
    icon: ['chat', 'send'][i],
    pos: [1, 3][i]
  }))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 110,
    className: "bh-care-phone"
  }, /*#__PURE__*/React.createElement(PhoneChat, {
    msgs: WA_BONI_MSGS,
    clock: "10:05"
  })), /*#__PURE__*/React.createElement(Reveal, {
    delay: 60,
    className: "bh-care-side bh-care-side--right"
  }, [1, 3].map((n, i) => /*#__PURE__*/React.createElement(CareCheck, {
    key: points[n],
    text: points[n],
    icon: ['note', 'clock'][i],
    pos: [2, 4][i]
  }))))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--white)",
    variant: 3
  }));
}
window.HowBridge = HowBridge;

// Thin-line icons for the care points. Hand-written rather than fetched from
// a CDN so they never fail to paint, and so the stroke can stay delicate on
// the dark band (the Too-Good-To-Go treatment: one light icon per point).
function CareIco({
  name
}) {
  const c = {
    width: 52,
    height: 52,
    viewBox: '0 0 32 32',
    fill: 'none',
    stroke: 'var(--brand)',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    style: {
      display: 'block'
    }
  };
  // Deliberately distinct from the FoodPlan step icons (bowl / chat / clock /
  // box) so no icon carries two meanings on one page. Each is drawn to fill
  // most of the viewBox, or it reads as a smudge at this size.
  if (name === 'note') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M25 17.5V27a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h9.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.5 20.5h6M10.5 24.5h9"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m22 4.6 5.4 5.4-9.2 9.2-6 .6.6-6z"
  }));
  if (name === 'photo') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("rect", {
    x: "3",
    y: "8",
    width: "26",
    height: "19",
    rx: "3"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "16",
    cy: "17.5",
    r: "5.2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.5 12.6h1.6"
  }));
  if (name === 'bell') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M16 4a8 8 0 0 0-8 8c0 6-2.5 8-2.5 8h21S24 18 24 12a8 8 0 0 0-8-8Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.6 24a2.6 2.6 0 0 0 4.8 0"
  }));
  if (name === 'door') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M8 28V5.5A1.5 1.5 0 0 1 9.5 4h13A1.5 1.5 0 0 1 24 5.5V28"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.5 28h23"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "20",
    cy: "16.5",
    r: "1.3"
  }));
  if (name === 'ruler') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("rect", {
    x: "2.5",
    y: "11",
    width: "27",
    height: "10",
    rx: "2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.5 11v4.4M16 11v6M22.5 11v4.4"
  }));
  if (name === 'send') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M29 4 3 15.4l9.4 3.8 3.8 9.4z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M29 4 12.4 19.2"
  }));
  // Added 2026-09-16 for the bridge's benefit points: leaf = the bags/park
  // good, chat = the WhatsApp channel. Same stroke, same fill-the-box rule.
  if (name === 'leaf') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M27 5C15 5 6.5 11.5 6.5 22.5c0 1.6.2 3 .6 4.3C18.5 27 27 19.5 27 5Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.5 27.5 18.5 14.5"
  }));
  // Added 2026-09-22 for the Boni pillar: "answers any time of day".
  if (name === 'clock') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("circle", {
    cx: "16",
    cy: "16",
    r: "12.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 8.5V16l5.2 3.2"
  }));
  if (name === 'chat') return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("path", {
    d: "M16 4.5c-7 0-12.5 4.8-12.5 10.8 0 3 1.4 5.7 3.7 7.6L6 28.5l6.3-2.8c1.2.3 2.4.4 3.7.4 7 0 12.5-4.8 12.5-10.8S23 4.5 16 4.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.5 14.2h11M10.5 18h7"
  }));
  return /*#__PURE__*/React.createElement("svg", c, /*#__PURE__*/React.createElement("circle", {
    cx: "16",
    cy: "16",
    r: "12.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12.4 10.6v10.8M19.6 10.6v10.8"
  }));
}

// The WhatsApp phone mockup. Skeuomorphic on purpose: palette, bubble
// geometry, wallpaper, read receipts, Dynamic Island and status bar are the
// REAL thing, because anything approximate reads as a fake screenshot. Two
// details do most of the work: the header is WHITE (the teal header was
// retired in 2021 and dates a mockup instantly), and the type is set at the
// true iOS ratio (~17pt of a 430pt-wide screen) rather than shrunk to fit.
// Assets (wa-wallpaper.png, wa-ticks.svg) are shared with the Hidden Worlds
// airport deck build. The avatar is the REAL one, lifted from a screenshot of
// Boni's live WhatsApp Business profile: cream disc, navy wordmark, pink heart.
// The old -v2 file was on the retired yellow palette and never matched what a
// customer actually sees in their chat list. Bubbles stay near-verbatim what the live system sends.
// THE HOMEPAGE PHONE IS IN THE BRIDGE (2026-09-16) and shows her FIRST
// message to a new member: hi, where she is, the dog's name, what happens
// next. It is the "how" in one glance and the exact opening the cold-arrival
// playbook has to deliver. Genderless (no "spreman"), no health invitation,
// and she promises only what is live: a food plan and a message before every
// delivery. Only ONE phone on the page; Journal now has its own card.
// ROUND 7 (2026-09-22): the onboarding conversation is gone. The section is
// no longer an introduction, so the phone has to show the CAPABILITY: you ask
// her something ordinary and the answer is about YOUR dog, with no context
// given. She knows he is a Labrador because she remembers, not because it was
// typed in this thread - that is the whole point of the section, shown rather
// than claimed. Care advice, never symptoms (the health boundary holds).
// BETA (2026-10-25): dog talk, not advice. She remembers who walks him (a
// detail from an earlier chat), gives one reaction, asks one easy question.
const WA_BONI_MSGS = [{
  side: 'out',
  text: 'Hugo danas opet nije htio van jer pada kiša 🙄',
  time: '10:02'
}, {
  side: 'in',
  text: 'Ni s Anom? Mislila sam da s njom ide i po potopu 😄',
  time: '10:02'
}, {
  side: 'out',
  text: 'Ni s njom! Sjedi na prozoru i gleda kišu kao da mu je nešto napravila',
  time: '10:04'
}, {
  side: 'in',
  text: 'Ozbiljno se ljuti na vrijeme, to mi je presmiješno 😂 Ima li tu svoje mjesto ili mijenja prozore?',
  time: '10:04'
}];
// Kept for the WhatsApp page, which still tells the delivery story.
const WA_HOME_MSGS = [{
  side: 'in',
  text: 'Hej! Samo da znaš, Hugova nova vreća stiže u četvrtak 🐾',
  time: '9:41'
}, {
  side: 'out',
  text: 'Zapravo, taj tjedan smo na putu.',
  time: '9:42'
}, {
  side: 'in',
  text: 'Nema problema! Mogu je pomaknuti malo ranije ili na sljedeći tjedan.',
  time: '9:42'
}, {
  side: 'out',
  text: 'Može srijeda idući tjedan. Hvala!',
  time: '9:43'
}, {
  side: 'in',
  text: 'Riješeno, stiže u srijedu 27.8. Pozdravi Huga 🐶',
  time: '9:43'
}];
// msgs/clock are props so other pages (the WhatsApp page) can show a different
// conversation in the same device without forking the component.
function PhoneChat({
  msgs = WA_HOME_MSGS,
  clock = '9:44'
}) {
  const bub = side => ({
    background: side === 'in' ? '#FFFFFF' : '#D9FDD3',
    alignSelf: side === 'in' ? 'flex-start' : 'flex-end',
    borderRadius: 12,
    [side === 'in' ? 'borderTopLeftRadius' : 'borderTopRightRadius']: 3,
    padding: '9px 12px 7px',
    maxWidth: '80%',
    boxShadow: '0 1px 0.5px rgba(11,20,26,.13)'
  });
  const txt = {
    margin: 0,
    fontFamily: 'var(--font-text)',
    fontSize: 19,
    lineHeight: 1.38,
    color: '#111B21',
    fontWeight: 500
  };
  const meta = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    marginTop: 2,
    fontFamily: 'var(--font-text)',
    fontSize: 14,
    color: '#667781',
    fontWeight: 500
  };
  const Ticks = () => /*#__PURE__*/React.createElement("img", {
    src: window.K + 'wa-ticks.svg',
    alt: "",
    width: "19",
    height: "13",
    style: {
      display: 'block'
    }
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-phone"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-phone-body"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-chrome"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-status"
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-wa-clock"
  }, clock), /*#__PURE__*/React.createElement("span", {
    className: "bh-wa-sysicons",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 20 12",
    width: "20",
    height: "12",
    fill: "#111B21"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "0",
    y: "8",
    width: "3",
    height: "4",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "4.8",
    y: "6",
    width: "3",
    height: "6",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "9.6",
    y: "3.5",
    width: "3",
    height: "8.5",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "14.4",
    y: "1",
    width: "3",
    height: "11",
    rx: "1"
  })), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 16 12",
    width: "16",
    height: "12",
    fill: "none",
    stroke: "#111B21",
    strokeWidth: "1.6",
    strokeLinecap: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M1.4 4.4a9 9 0 0 1 13.2 0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.1 7.3a5.2 5.2 0 0 1 7.8 0"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "10.3",
    r: "0.9",
    fill: "#111B21",
    stroke: "none"
  })), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 26 13",
    width: "26",
    height: "13",
    fill: "none"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "0.7",
    y: "0.7",
    width: "21",
    height: "11.6",
    rx: "3.4",
    stroke: "#111B21",
    strokeOpacity: "0.4",
    strokeWidth: "1.2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "2.4",
    y: "2.4",
    width: "15",
    height: "8.2",
    rx: "2.2",
    fill: "#111B21"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.4 4.6v3.8a2.6 2.6 0 0 0 0-3.8z",
    fill: "#111B21",
    fillOpacity: "0.45"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-top"
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-wa-back",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-chat-avatar-v3.png',
    alt: "Boni",
    loading: "lazy",
    decoding: "async",
    className: "bh-wa-av"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-name"
  }, "Boni"), /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-sub"
  }, "online")))), /*#__PURE__*/React.createElement("span", {
    className: "bh-island",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wa-scr"
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-wa-day"
  }, "danas"), msgs.map((m, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: bub(m.side)
  }, m.img ? /*#__PURE__*/React.createElement("img", {
    src: m.img,
    alt: "",
    loading: "lazy",
    decoding: "async",
    style: {
      display: 'block',
      width: '100%',
      aspectRatio: '4 / 3',
      objectFit: 'cover',
      borderRadius: 8,
      marginBottom: 6
    }
  }) : null, /*#__PURE__*/React.createElement("p", {
    style: txt
  }, m.text), /*#__PURE__*/React.createElement("div", {
    style: meta
  }, m.time, m.side === 'out' && /*#__PURE__*/React.createElement(Ticks, null)))))));
}
function CareCheck({
  text,
  icon,
  pos
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: 'bh-care-check bh-care-check--' + pos
  }, /*#__PURE__*/React.createElement(CareIco, {
    name: icon
  }), /*#__PURE__*/React.createElement("span", {
    className: "bh-care-txt"
  }, text));
}

// THE THREE PILLARS ARE ONE "HOW WE DO IT" LINE (2026-09-12, user): after the
// hero (what we promise) and the villain (why), the reader needs to see HOW,
// and the three sections below are that answer, numbered 01 · 02 · 03.
// RULE FOR EVERY PILLAR HEADLINE: it carries the GOAL, never the feature.
// Not "Hrana stiže sama" but "we sorted the food SO THAT you worry less and
// have more time with him". Shape: [what we did] + brand-coloured [so that…].
// The numbered eyebrow's label is the goal too, and the first two are the hero
// promise split in half: "Više trenutaka" = Journal, "Manje brige" = the food
// plan (the promise line survives as architecture, boni_brand_core.md). All
// three numerals are brand violet on purpose — the numbering is connective
// tissue, so it must not change colour per section.
// HowRail — the connective tissue of the "how we do it" line, round 4 (user:
// the in-page paw trails "take too much space", wanted "something on the
// scrollbar on the side"). A slim rail PINNED to the right edge, desktop only,
// visible only while the reader is inside the bridge -> 03 run: Boni's avatar
// on top, then 01 · 02 · 03 as small numerals joined by a line, and a paw that
// slides down the line as you scroll (t = 0 at the bridge, 1/2/3 at the three
// pillar centres, interpolated). Zero page height; the section stickers still
// carry the count in the page itself. Clicking a numeral scrolls to it.
// Phones render nothing (display:none in CSS) - the stack is already linear.
const RAIL_IDS = ['journal', 'plan-dostave', 'boni', 'dobro-oko-tebe'];
// The goal labels moved OFF the page and onto the rail (user, round 5: the rail
// is visible enough, so the in-section 01/02/03 stickers are hidden at desktop
// and survive only on phones, where the rail cannot exist).
const RAIL_LABELS = ['Journal', 'Plan dostave', 'Boni', 'Vrećice'];
function HowRail() {
  const rootRef = React.useRef(null);
  const [state, setState] = React.useState({
    on: false,
    t: 0
  });
  React.useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = 0;
      const els = RAIL_IDS.map(id => document.getElementById(id));
      if (els.some(e => !e)) return;
      const mid = window.scrollY + window.innerHeight * 0.5;
      const cs = els.map(e => {
        const r = e.getBoundingClientRect();
        return window.scrollY + r.top + r.height / 2;
      });
      const first = els[0].getBoundingClientRect(),
        last = els[3].getBoundingClientRect();
      // on while the run covers the middle band of the viewport (not the exact
      // centre line: 03 is shorter than half a screen, so the centre test hid
      // the rail while 03 was plainly on screen)
      const vh = window.innerHeight;
      const on = first.top <= vh * 0.65 && last.bottom >= vh * 0.35;
      let t = 0;
      if (mid <= cs[0]) t = 0;else if (mid >= cs[3]) t = 3;else {
        for (let i = 0; i < 3; i++) if (mid >= cs[i] && mid < cs[i + 1]) {
          t = i + (mid - cs[i]) / (cs[i + 1] - cs[i]);
          break;
        }
      }
      setState(p => p.on === on && Math.abs(p.t - t) < 0.002 ? p : {
        on,
        t
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    tick();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // paw y: interpolate between the stops' centres, measured off the DOM
  let pawTop = 0;
  if (rootRef.current) {
    const stops = rootRef.current.querySelectorAll('[data-stop]');
    if (stops.length === 4) {
      const y = el => el.offsetTop + el.offsetHeight / 2;
      const i = Math.min(2, Math.floor(state.t)),
        f = state.t - i;
      pawTop = y(stops[i]) + (y(stops[i + 1]) - y(stops[i])) * f;
    }
  }
  const active = Math.round(state.t);
  const go = id => e => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return /*#__PURE__*/React.createElement("nav", {
    ref: rootRef,
    className: 'bh-rail' + (state.on ? ' bh-rail--on' : ''),
    "aria-label": "Kako to radimo"
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-rail-av bh-rail-av--mark",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-chat-avatar-v3.png',
    alt: "",
    loading: "lazy",
    decoding: "async"
  })), ['01', '02', '03', '04'].map((n, i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: n
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-rail-seg",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("a", {
    href: '#' + RAIL_IDS[i],
    onClick: go(RAIL_IDS[i]),
    className: 'bh-rail-num' + (active === i ? ' is-active' : ''),
    "data-stop": true
  }, n, /*#__PURE__*/React.createElement("span", {
    className: "bh-rail-lbl"
  }, RAIL_LABELS[i])))), /*#__PURE__*/React.createElement(PawPrint, {
    size: 18,
    color: "var(--coral-400)",
    className: "bh-rail-paw",
    style: {
      top: pawTop
    }
  }));
}
window.HowRail = HowRail;

// Secondary CTA closing each pillar (feedback 2026-09-15: "on the features
// section we should have a CTA on each one that either gives you more info or
// says test it, so if you are interested in that feature we get you there").
// DESTINATIONS ARE DELIBERATELY NOT WIRED (user: "for the ctas now just make
// the buttons, we will make where they go later"). Each button is a no-op
// carrying a data-cta hook, so pointing one somewhere later is a one-line
// change and nothing navigates to a 404 in the meantime.
// NOT the DS primary Button on purpose: the page has exactly one primary
// action (Učlani se). These are "show me more", so they read as outlined.
// Always violet — the palette rule is that CTAs are Vivid Violet, and violet
// holds contrast on all three pillar grounds (blush, white, pistachio).
function PillarCta({
  label,
  id,
  href
}) {
  // href wires a CTA to a REAL page (2026-09-19: 02 -> the catalog). The
  // others stay inert buttons until their pages exist.
  const inner = /*#__PURE__*/React.createElement(React.Fragment, null, label, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "→"));
  if (href) return /*#__PURE__*/React.createElement("a", {
    className: "bh-pillar-cta",
    "data-cta": id,
    href: href
  }, inner);
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "bh-pillar-cta",
    "data-cta": id
  }, inner);
}
window.PillarCta = PillarCta;

// PHONE-ONLY (hidden at >=900px, where the fixed side rail carries the numbers).
// NO EYEBROW LABEL (user, 2026-09-23): the small-caps product name above the
// headline was a second label saying what the headline already says, and on a
// 390px screen it pushed the title down for nothing. The numeral alone is enough
// - it is the only thing the rail contributes that the section cannot say for
// itself. `label` is still accepted because every call site passes it and the
// rail's own RAIL_LABELS are a separate list; it is deliberately not rendered.
function PillarTag({
  n,
  label,
  align = 'center'
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: 'bh-pillar-tag' + (align === 'left' ? ' bh-pillar-tag--left' : '')
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-pillar-num"
  }, n));
}

// RHYTHM CARD — the FoodPlan graphic: a calendar month with the delivery day
// marked, and ONE WhatsApp exchange that moves it a week. Shows "less worry"
// and "one message" in a single picture without putting a second phone on the
// page (the Journal owns the phone; two phones = the same UI twice). Every
// element is a real capability: the 4-week rhythm, the 7-day nudge, delay by
// message. September 2026 is a real month (the 1st is a Tuesday) and 10.9. and
// 17.9. are both Thursdays, so the move reads as "a week later" at a glance
// (round 4, user: 10 and 17, so both days sit high in the grid and the reply
// bubble does not cover the moved day).
function RhythmCard() {
  // Round 2 (user, 2026-09-19): the bubbles are the main thing, the calendar
  // supports them. Boni's reminder FIRST (white, top-left, shadow over the
  // calendar), the owner's reply second (green, bottom-right) with a thumbs-up
  // reaction instead of a confirmation bubble. The 7-day chip is gone: it was
  // pulling the eye off the message. The struck day is a big coral X (CSS).
  const dow = ['P', 'U', 'S', 'Č', 'P', 'S', 'N'];
  const cells = [null, ...Array.from({
    length: 30
  }, (_, i) => i + 1)]; // Sep 1 2026 = Tuesday
  const OLD = 10,
    NEW = 17;
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm-cal",
    role: "img",
    "aria-label": "Kalendar: dostava pomaknuta s 10. na 17. rujna jednom porukom"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-rhythm-month"
  }, "Rujan"), /*#__PURE__*/React.createElement("span", {
    className: "bh-rhythm-every"
  }, "Svaka 4 tjedna")), /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm-grid",
    "aria-hidden": "true"
  }, dow.map((d, i) => /*#__PURE__*/React.createElement("span", {
    key: 'd' + i,
    className: "bh-rhythm-dow"
  }, d)), cells.map((n, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    className: 'bh-rhythm-day' + (n === null ? ' bh-rhythm-day--empty' : n === OLD ? ' bh-rhythm-day--old' : n === NEW ? ' bh-rhythm-day--new' : '')
  }, n)))), /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm-bub bh-rhythm-bub--in"
  }, /*#__PURE__*/React.createElement("p", null, "Hej! Hugova hrana stiže u četvrtak 10.9. Je li sve ok? 🐾"), /*#__PURE__*/React.createElement("span", null, "9:41")), /*#__PURE__*/React.createElement("div", {
    className: "bh-rhythm-bub bh-rhythm-bub--out"
  }, /*#__PURE__*/React.createElement("p", null, "Taj tjedan smo na putu. Može tjedan kasnije?"), /*#__PURE__*/React.createElement("span", null, "9:52 ", /*#__PURE__*/React.createElement("img", {
    src: window.K + 'wa-ticks.svg',
    alt: "",
    width: "17",
    height: "12"
  })), /*#__PURE__*/React.createElement("em", {
    className: "bh-rhythm-react",
    "aria-label": "Boni je reagirala palcem gore"
  }, "👍")));
}

// 3 · JOURNAL — pillar 01: creating and keeping moments. Beneficiary: you AND
// the dog. Keeps the Too-Good-To-Go composition (phone centred, two checks each
// side) because there are exactly four points.
// Everything lives in the WhatsApp thread — no app, no feed, no timeline UI —
// which is also the first objection anyone has to a journaling product, so the
// section says it outright.
// COPY SHAPE (user, ~6 rounds, 2026-09-12): a plain statement in OUR voice
// ("našim ljubimcima" = we are pet parents too), then "Zato ti pomažemo…" on
// its own line — the founder move, "we had this, so we made this", not a
// pitch. The name Journal is NOT in the h2 (nobody knows the word yet); the
// context line introduces it with its origin: "Tako je nastao Journal."
// Words that were tried and cut: "dragocjeno" (precious = afraid of losing
// him, the section must feel like enjoying today, not preparing for the end),
// "s njim" as our way of naming the dog (reads like someone you do not care
// about), "kreirati / iskustva / memorije" (borrowed words are what make a
// sentence sound like a pitch; "doživljaj" is the native, exciting one).
// 01 · JOURNAL (2026-09-16): the phone moved to the bridge, so Journal gets
// its OWN object — a journal card: one saved moment (photo + date + your note)
// with Boni's "spremljeno" bubble on it. It shows the RESULT of Journal rather
// than the chat that produces it, and the page keeps exactly one phone.
// Copy in the page's founder shape: the routine we noticed, "pa smo" the
// thing we built. Journal is a NUDGE, not a folder (storage is a solved
// problem; routine and the days disappearing is the real reason, S104).
// The photo is the point-of-view park shot generated for the villain trial
// (about-pov-dog-park-v1.webp): one dog, one ball, one moment, nobody else.
function JournalCard() {
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-jcard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-jcard-paper"
  }, /*#__PURE__*/React.createElement("img", {
    className: "bh-jcard-photo",
    src: window.K + 'about-pov-dog-park-v1.webp',
    alt: "Pas s lopticom u parku gleda gore prema svom pet parentu",
    loading: "lazy",
    decoding: "async"
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-jcard-row"
  }, /*#__PURE__*/React.createElement(Chip, {
    rotate: -4,
    bg: "var(--yellow-400)",
    style: {
      flex: 'none'
    }
  }, "Sub, 12.9."), /*#__PURE__*/React.createElement("p", {
    className: "bh-jcard-note"
  }, "Prvi put mi je sam donio lopticu natrag. Pa je legao na nju. 😄"))), /*#__PURE__*/React.createElement("div", {
    className: "bh-jcard-bub",
    "aria-hidden": "true"
  }, "Spremljeno u Journal 🐾"));
}
// JOURNAL CHAT (2026-09-19, round 2, user): the Journal graphic is the LOOP as
// WhatsApp bubbles, no phone bezel (the bridge owns the phone): Boni nudges ->
// you send a photo + a line -> she saves it and points at the weekly recap.
// The recap link is there so the reader sees she CAN do that; the recap itself
// is not built (honesty ledger). The "photo" is an illustration on purpose:
// site-wide rule since S106, no AI images of real things.
// ROUND 7 (2026-09-22, user: the old exchange read unrealistic and robotic).
// She opens the way a friend does - asks how he is and whether there are new
// pictures - the reply is an ordinary sentence with a photo attached, and she
// reacts to the photo BEFORE she does anything useful with it. The times run
// in sequence now; the old script jumped from 18:40 to 11:12.
// BETA (2026-10-25): rewritten to what beta Boni actually does. She does not
// open with a nudge (the beta is reactive, apart from a rare template), she
// reacts to ONE specific thing, asks ONE easy question, and the payoff is a
// real page in the Journal, not a promised recap. Voice rules:
// memory/feedback_boni_chat_voice.md.
const JCHAT = [{
  side: 'out',
  img: window.K + 'journal-park-illu-v2.webp',
  text: 'Prvi put na Bundeku, odmah je našao nečiju lopticu 😂',
  time: '17:24'
}, {
  side: 'in',
  text: 'Tuđa loptica je uvijek bolja 😄 Je li je vratio ili je sad njegova?',
  time: '17:24'
}, {
  side: 'out',
  text: 'Naravno da je nije vratio',
  time: '17:25'
}, {
  side: 'in',
  text: 'Hugo ima novu stranicu u Journalu 📖',
  link: 'Hugov Journal →',
  time: '17:26'
}];
function JournalChat() {
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-jchat",
    role: "img",
    "aria-label": "WhatsApp razgovor: pošalješ fotku psa, Boni se oduševi i od toga napiše novu stranicu u Journalu"
  }, JCHAT.map((m, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    className: 'bh-jchat-bub bh-jchat-bub--' + m.side + ' bh-jchat-bub--' + i
  }, m.img ? /*#__PURE__*/React.createElement("img", {
    className: "bh-jchat-img",
    src: m.img,
    alt: "",
    loading: "lazy",
    decoding: "async"
  }) : null, /*#__PURE__*/React.createElement("p", null, m.text), m.link ? /*#__PURE__*/React.createElement("p", {
    className: "bh-jchat-link"
  }, m.link) : null, /*#__PURE__*/React.createElement("span", null, m.time, m.side === 'out' ? /*#__PURE__*/React.createElement("img", {
    src: window.K + 'wa-ticks.svg',
    alt: "",
    width: "17",
    height: "12"
  }) : null))));
}
function Journal() {
  // BETA (2026-10-25): every line is something the beta does today. The old
  // "Boni ti se javi i podsjeti te da probate nešto novo" was cut: beta Boni
  // does not write first with ideas.
  const checks = ['Pošalješ fotku ili ispričaš nešto, ostalo piše Boni', 'Prva stranica nastaje već od prve fotke', 'Sve na jednoj privatnoj stranici, poredano po danima', 'Nakon godinu dana imaš priču, ne samo fotografije'];
  return /*#__PURE__*/React.createElement("section", {
    id: "journal",
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--coral-50)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 104,
    rotate: 22,
    opacity: 0.28,
    color: "var(--berry-300)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      bottom: 58,
      left: -26
    }
  }), /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--brand)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 70,
      left: '7%'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-split"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement(PillarTag, {
    n: "01",
    label: "Journal",
    align: "left"
  }), /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      margin: '0 0 14px'
    }
  }, "Tvoj pas ima svoju priču.", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "Boni je zapisuje.")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: 'var(--font-text)',
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.55,
      color: 'var(--text-body)',
      margin: 0
    }
  }, "Pričaš s Boni na WhatsAppu kao s prijateljem, a ona od toga piše Journal, osobni dnevnik o tvom psu."), /*#__PURE__*/React.createElement("div", {
    className: "bh-good-points"
  }, checks.map(p => /*#__PURE__*/React.createElement("div", {
    key: p,
    className: "bh-good-point"
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 18,
    opacity: 1,
    color: "var(--brand)",
    style: {
      flex: 'none',
      marginTop: 3
    }
  }), /*#__PURE__*/React.createElement("span", null, p))))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 90
  }, /*#__PURE__*/React.createElement(JournalChat, null))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-50)",
    variant: 2
  }));
}
window.Journal = Journal;

// 4 · FOODPLAN — pillar 02: less worry. Beneficiary: you.
// Was four icon cards (read as "bland, no graphics" — user). Now a split:
// the RhythmCard graphic on the LEFT, copy on the right, so 02 alternates
// against 01 (centred phone) and 03 (text left, photo right). DOM order stays
// text-first so a phone stacks copy → graphic; `.bh-split--flip` swaps them on
// desktop only.
// This is the most TRUE section on the page: every line describes something
// running in production today, including the seven-day nudge — except the free
// shipping, which has no rate behind it yet (only real domestic rate is 4,00
// EUR). Make it true or cut that line before real traffic.
// NOTE: food is a PERK of membership here, never the pitch. If this section
// ever starts reading like the thing we sell, the repositioning has leaked.
function FoodPlan() {
  const points = ['Plan u ritmu tvog psa. Ni previše, ni premalo.', 'Pomakni, preskoči ili pauziraj jednom porukom.',
  // 2026-09-22 (user): "prva dostava uz 20% popusta" is an OFFER sitting in a
  // list of features, so it read as odd here; it moved to the membership card
  // in JoinClose, where offers belong. This slot now states the member perk.
  // WARNING, unchanged since S103: free shipping is a DECIDED member perk but
  // has no Shopify rate behind it (only real domestic rate is 4,00 EUR).
  // Configure the rate before real traffic or this line is a promise we
  // cannot honour at checkout.
  'Besplatna dostava na tvoja vrata.'];
  return /*#__PURE__*/React.createElement("section", {
    id: "plan-dostave",
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 84,
    rotate: -14,
    opacity: 0.09,
    color: "var(--teal-600)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 24,
      right: -20
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-split bh-split--flip"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement(PillarTag, {
    n: "02",
    label: "Plan dostave",
    align: "left"
  }), /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      margin: '0 0 14px'
    }
  }, "Prepusti brigu o hrani", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "Boni.")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: 'var(--font-text)',
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.55,
      color: 'var(--text-body)',
      margin: 0
    }
  }, "Hrana stiže sama, u ritmu tvog psa. Kad se nešto promijeni, jedna poruka i riješeno."), /*#__PURE__*/React.createElement("div", {
    className: "bh-good-points"
  }, points.map(p => /*#__PURE__*/React.createElement("div", {
    key: p,
    className: "bh-good-point"
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 18,
    opacity: 1,
    color: "var(--brand)",
    style: {
      flex: 'none',
      marginTop: 3
    }
  }), /*#__PURE__*/React.createElement("span", null, p)))), /*#__PURE__*/React.createElement(PillarCta, {
    label: "Pogledaj hranu",
    id: "food",
    href: "/collections/sva-hrana"
  })), /*#__PURE__*/React.createElement(Reveal, {
    delay: 90
  }, /*#__PURE__*/React.createElement(RhythmCard, null))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-50)",
    variant: 0
  }));
}
window.FoodPlan = FoodPlan;
// 3b · HOWITWORKS — the process: four icon steps, one line each. The catalog
// card's "Kako radi?" link resolves here (#kako-radi). Ends with the
// free-with-a-plan line + CTA because this is the first point where the
// reader knows enough to decide.
function HowItWorks() {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  const steps = [{
    icon: "match",
    color: "var(--coral-400)",
    bg: "var(--coral-50)",
    title: "Odaberi hranu",
    text: "Iz kataloga najboljih brendova za pse."
  }, {
    icon: "calendar",
    color: "var(--yellow-500)",
    bg: "var(--yellow-50)",
    title: "Složimo plan dostave",
    text: "Koliko hrane i koliko često, po mjeri tvog psa."
  }, {
    icon: "chat",
    color: "var(--teal-600)",
    bg: "var(--teal-100)",
    title: "Pridružiš se Boni WhatsAppu",
    text: "Tamo dogovaraš i mijenjaš sve u par poruka."
  }, {
    icon: "box",
    color: "var(--sand-600)",
    bg: "var(--sand-50)",
    title: "Hrana stiže sama",
    text: "Na vrata, točno prije nego što ponestane."
  }];
  return /*#__PURE__*/React.createElement("section", {
    id: "kako-radi",
    className: "bh-section bh-section--wave",
    style: {
      background: "var(--white)"
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 84,
    rotate: -14,
    opacity: 0.09,
    color: "var(--teal-600)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      top: 24,
      right: -20
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap",
    style: {
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      margin: "0 auto var(--space-6)",
      textAlign: "center"
    }
  }, "Kako radi?")), /*#__PURE__*/React.createElement(Reveal, {
    delay: 80
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-how-grid"
  }, steps.map((s, i) => /*#__PURE__*/React.createElement("div", {
    key: s.title,
    className: "bh-step-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-step-icon",
    style: {
      background: s.bg
    }
  }, /*#__PURE__*/React.createElement(DoodleIcon, {
    name: s.icon,
    size: 38,
    color: s.color
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-text)",
      fontWeight: 800,
      fontSize: 12,
      letterSpacing: ".08em",
      color: "var(--ink-500)",
      marginBottom: 5
    }
  }, "KORAK ", i + 1), /*#__PURE__*/React.createElement("h3", {
    style: {
      fontFamily: "var(--font-display)",
      fontWeight: 700,
      fontSize: 20,
      color: "var(--ink-900)",
      margin: "0 0 7px",
      lineHeight: 1.2
    }
  }, s.title), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "var(--font-text)",
      fontWeight: 600,
      fontSize: 15,
      lineHeight: 1.5,
      color: "var(--ink-700)",
      margin: 0
    }
  }, s.text)))))), /*#__PURE__*/React.createElement("div", {
    className: "bh-cta-row",
    style: {
      marginTop: "var(--space-6)"
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "lg",
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: DARK_BTN,
    onClick: openBoniWizard
  }, "Postani član"), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "var(--font-text)",
      fontWeight: 700,
      fontSize: 15,
      color: "var(--ink-700)"
    }
  }, "Članstvo je besplatno uz plan dostave."))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-50)",
    variant: 3
  }));
}
window.HowItWorks = HowItWorks;

// 5 · GOODDEED — the club's good deed, on the PISTACHIO band (pistachio = the
// good). THE CAUSE IS DOGS BEING WELCOME, not the environment: mess is the most
// common reason dogs get banned from shared space, so picking up is what keeps
// parks open to them. "Biorazgradive" is deliberately DEMOTED to a supporting
// point, and the material claim itself is PRINTED ON THE PACK in the photo as a
// Boni-owned mark — deliberately NOT a third-party certification seal (OK compost /
// EN 13432 Seedling are registered certification marks; using one uncertified would
// be both misleading and trademark misuse). Swap in the real mark once certified.
// If the bags turn out to be bioplastic only that one word changes, and
// we never make a claim about the FOOD's footprint (we sell meat, that flank
// stays closed). "Pomozi nam" keeps it a shared project: the mess is made by
// dog owners, i.e. by our own customers, so this must never read as a lecture.
function GoodDeed() {
  // Round 2 (user, 2026-09-16): the park-closure framing (dogs banned from
  // parks over mess) is a fact many Croatians will not recognise, and "misliti
  // na park" needed it to make sense. Reframed on the walk itself and on the
  // bags not staying in nature. "Biorazgradive" is a claim the bags must back
  // (EN 13432, S78 rule); user confirms they do.
  const points = ["Vrećice za svaku šetnju, uvijek pri ruci.", "Biorazgradive, pa ne ostaju u prirodi godinama.", "Svatko od nas doprinosi, vrećicu po vrećicu."];
  return /*#__PURE__*/React.createElement("section", {
    id: "dobro-oko-tebe",
    className: "bh-section bh-section--wave",
    style: {
      background: "var(--teal-100)"
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 90,
    rotate: -18,
    opacity: 0.14,
    color: "var(--teal-600)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      top: 18,
      right: -22
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-split"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement(PillarTag, {
    n: "04",
    label: "Vrećice",
    align: "left"
  }), /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2 bh-good-h2"
  }, "Pazi na okoliš, ", /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "bez dodatnog truda.")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: 'var(--font-text)',
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.55,
      color: 'var(--text-body)',
      margin: '14px 0 0'
    }
  }, "Uz svaku dostavu dobivaš biorazgradive vrećice za pse. Ti samo pokupiš za svojim psom."), /*#__PURE__*/React.createElement("div", {
    className: "bh-good-points"
  }, points.map(p => /*#__PURE__*/React.createElement("div", {
    key: p,
    className: "bh-good-point"
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 18,
    opacity: 1,
    color: "var(--teal-600)",
    style: {
      flex: "none",
      marginTop: 3
    }
  }), /*#__PURE__*/React.createElement("span", null, p)))), /*#__PURE__*/React.createElement(PillarCta, {
    label: "Saznaj više",
    id: "bags"
  })), /*#__PURE__*/React.createElement(Reveal, {
    delay: 90
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-good-elem"
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'club-boni-bags-roll-v2.webp',
    alt: "Ilustracija: rola biorazgradivih Boni vrećica",
    loading: "lazy",
    decoding: "async"
  })))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--white)",
    variant: 1
  }));
}
window.GoodDeed = GoodDeed;

// 6 · TESTIMONIALS — "Evo što prvi članovi kažu." The dogs are the members, and
// the quotes follow the member arc: came for the delivery, found the people,
// stayed for the pride, now telling everyone.
// R2: trimmed 5 → 3 (user: section was too big/long). The three keep the arc
// stages the featured Marko card doesn't already carry: came-for-delivery
// (Ana), bandana pride (Lucija), donation pride (Petra). Ivana + Tomislav
// retired (community + skeptic→advocate both live inside Marko's story).
const TESTI = [{
  name: "Ana K.",
  dog: "Bruno",
  parent: "Brunova mama",
  breed: "Labrador",
  slug: "ana",
  tone: "teal",
  text: "Došla sam zbog dostave. Sad se subotom nalazimo s još pet pasa iz Kluba."
}, {
  name: "Lucija B.",
  dog: "Lola",
  parent: "Lolina mama",
  breed: "Francuski buldog",
  slug: "lucija",
  tone: "berry",
  text: "Lola nosi svoju bandanu po parku ko medalju. Ljudi nas stvarno zaustavljaju i pitaju."
},
// NOTE: never reinstate the 'petra' photo — it shows a real Pedigree bag
// (competitor brand) + a messy floor, directly under the best-brands pitch.
{
  name: "Tomislav R.",
  dog: "Rio",
  parent: "Rijev tata",
  breed: "Border collie",
  slug: "tomislav",
  tone: "leaf",
  text: "Bio sam skeptičan prema svemu online. Sad ja cijelom kvartu šaljem Bonijev broj."
}];

// The featured review — the full arc in one voice: need → people → role → advocacy.
const FEATURED_REVIEW = {
  name: "Marko V.",
  dog: "Loki",
  parent: "Lokijev tata",
  breed: "Sibirski haski",
  slug: "marko",
  text: "Pretplatio sam se jer mi je dosadilo vući vreće iz dućana poslije posla. Mislio sam da je to sve. Onda je netko u parku prepoznao Lokijevu bandanu, pa smo završili na zajedničkoj šetnji. Prošli mjesec smo svi skupa odnijeli višak hrane u sklonište. Sad o Klubu pričam svima, jer ovako nešto kod nas još nije postojalo."
};
const PIN_TILT = [3.5, -2, 0.5, -2.5, 2];
function ReviewCard({
  t,
  i
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-testi-card"
  }, /*#__PURE__*/React.createElement(Pushpin, {
    size: 26,
    color: "var(--yellow-400)",
    rotate: PIN_TILT[i],
    style: {
      position: "absolute",
      top: -16,
      left: "50%",
      marginLeft: -13,
      zIndex: 3
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "var(--surface-raised)",
      border: "1px solid var(--hair)",
      borderRadius: "var(--radius-lg)",
      boxShadow: "var(--shadow-sticker)",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      borderBottom: "1px solid var(--hair)"
    }
  }, /*#__PURE__*/React.createElement(Photo, {
    src: window.K + `reviews-${t.slug}-v2.jpeg`,
    tone: t.tone,
    ratio: "1 / 1",
    radius: "0",
    caption: t.breed
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "12px 14px 15px"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: "var(--yellow-500)",
      fontSize: 14,
      letterSpacing: 1
    }
  }, "★★★★★"), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "var(--font-text)",
      fontSize: 13,
      lineHeight: 1.5,
      color: "var(--ink-700)",
      margin: "6px 0 10px"
    }
  }, "„", t.text, "”"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-display)",
      fontWeight: 700,
      fontSize: 14.5,
      color: "var(--ink-900)"
    }
  }, t.name), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-text)",
      fontSize: 12,
      color: "var(--ink-500)",
      fontWeight: 700
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 11,
    opacity: 1,
    color: `var(--${t.tone}-400)`,
    style: {
      display: "inline-block",
      verticalAlign: -1,
      marginRight: 4
    }
  }), t.parent, " · članovi Boni Cluba"))));
}
function Testimonials() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section",
    style: {
      background: "var(--white)"
    }
  }, /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--berry-400)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      top: 70,
      left: "14%"
    }
  }), /*#__PURE__*/React.createElement(Sparkle, {
    size: 24,
    color: "var(--yellow-400)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      top: 130,
      right: "12%"
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      marginBottom: "var(--space-6)"
    }
  }, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2"
  }, "Evo što prvi članovi kažu."))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 70
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 780,
      margin: "0 auto 26px",
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement(Pushpin, {
    size: 29,
    color: "var(--yellow-400)",
    style: {
      position: "absolute",
      top: -17,
      left: "50%",
      marginLeft: -14.5,
      zIndex: 3
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-featured"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-featured-img"
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'reviews-marko-v2.jpeg',
    alt: FEATURED_REVIEW.breed,
    loading: "lazy",
    decoding: "async",
    style: {
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover"
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "24px 28px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: "var(--yellow-500)",
      fontSize: 16,
      letterSpacing: 1.5
    }
  }, "★★★★★"), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "var(--font-text)",
      fontSize: 15.5,
      lineHeight: 1.6,
      color: "var(--ink-700)",
      margin: "9px 0 13px"
    }
  }, "„", FEATURED_REVIEW.text, "”"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-display)",
      fontWeight: 700,
      fontSize: 16,
      color: "var(--ink-900)"
    }
  }, FEATURED_REVIEW.name), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-text)",
      fontSize: 13,
      color: "var(--ink-500)",
      fontWeight: 700
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 12,
    opacity: 1,
    color: "var(--yellow-500)",
    style: {
      display: "inline-block",
      verticalAlign: -1,
      marginRight: 4
    }
  }), FEATURED_REVIEW.parent, " · članovi Boni Cluba"))))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 120
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-testi-rail"
  }, TESTI.map((t, i) => /*#__PURE__*/React.createElement(ReviewCard, {
    key: t.name,
    t: t,
    i: i
  }))))));
}
window.Testimonials = Testimonials;

// 7 · JOINCLOSE — the ask as its OWN chapter: a navy "membership card" panel
// on a white section (user: the close must not blend into the reviews).
// One message, one button, and the catalog door stays open.
// JOINCLOSE (2026-09-12, round 6, user): the panel is a logo + one line + the
// payoff + one button. The "Boni Family" logotype sits in the top-left corner
// (built in the Boni Club construction: the hand-drawn Boni wordmark + "Family"
// in Fredoka 600, kerned, pistachio; assets/boni-family-card.svg, sources in
// branding/boni-family-logo*.svg), so the h2 no longer has to carry the name.
// The price line and the "Samo trebaš hranu? Pogledaj katalog" link were cut
// (2026-09-12); contents came BACK 2026-09-19 as the menu card list below.
// What the membership CONTAINS, as a menu card lists what is in the dish.
// Free shipping is deliberately NOT repeated here (02 already claims it and it
// still has no rate behind it). Price is absent on purpose: Ante's call.
// MEMBERSHIP_PRICE + ACCESS_LABEL now live in Shared.jsx so the hero caption
// and this card can never state different numbers. Do NOT re-declare them
// here: a duplicate top-level const kills the whole bundle (S42).
// ORDER IS THE ARGUMENT (2026-09-22 round 2, on feedback the user relayed):
// the three lines with a EURO VALUE come FIRST, because by the close the only
// open question is whether 8,99 EUR is fair, and free delivery (~4 EUR a drop)
// + bags (~1 EUR) + the 20% roughly cover the fee on their own. Boni and the
// Journal follow: the rest of the page has already sold them, and they are
// what you actually get rather than what pays for the fee.
// "Plan dostave hrane" was dropped (user's list) - "uz svaku narudzbu hrane"
// already implies the ordering, and a shorter card is the point.
// Each line is a THING you get, not a sentence about it. One claim per line:
// the delivery and the bags used to share a line and it ran too long.
// BETA (2026-10-25): only what the beta contains. Food, delivery, bags and
// the 20% are not part of rani pristup, so they are off the card.
const JOIN_INSIDE = ['Boni, asistentica na WhatsAppu', 'Journal, dnevnik o tvom psu', 'Privatna stranica samo za tebe', 'Prilika da s nama oblikuješ Boni'];
function JoinClose() {
  // EARLY ACCESS, NOT A CHECKOUT (2026-09-22, user).
  //
  // ROUND 2 REVERSED ONE THING ROUND 1 DID, and the reason it is fine: the
  // tag sits back ON the card, but BESIDE THE LOGO rather than floating above
  // the list. Round 1 moved it out because on the card it read as a badge on
  // a product; next to the wordmark it reads as the STATE OF BONI FAMILY, the
  // way a version label does, which is exactly what it is.
  //
  // THE CARD IS READ TOP-DOWN AS AN ARGUMENT: what it is (logo + tag) -> what
  // it costs -> a rule -> what that buys, value lines first -> apply. The
  // price sits directly under the name because the number is the thing a
  // reader at the close is actually weighing, and burying it under six
  // bullets made them scan the bullets twice. The divider is what lets the
  // list read as the ANSWER to the price rather than more of the same block.
  //
  // The "Sto dobivas kad udes" eyebrow is gone: with the price above it and a
  // divider between, the list needs no label, and the card was crowded.
  //
  // STILL NOT BUILT: the free month has no Recharge mechanics, free shipping
  // has no Shopify rate behind it, and the queue is a human reading
  // applications. Two of the five lines are therefore promises, not features.
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  return /*#__PURE__*/React.createElement("section", {
    id: "clanstvo",
    className: "bh-section bh-section--wave",
    style: {
      background: "var(--white)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-close-grid"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      margin: '0 0 26px'
    }
  }, "Budi među", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'relative',
      display: 'inline-block'
    }
  }, "prvim članovima", /*#__PURE__*/React.createElement(Squiggle, {
    color: "var(--coral-400)",
    width: "100%",
    height: 16,
    style: {
      position: 'absolute',
      left: 0,
      bottom: -13,
      width: '100%'
    }
  })), "."), /*#__PURE__*/React.createElement("p", {
    className: "bh-close-sub"
  }, "Boni Family trenutno testiramo s manjom grupom i polako primamo nove ljude."), /*#__PURE__*/React.createElement("p", {
    className: "bh-close-sub"
  }, "Prijavi se i ispričaj nam svoju priču. Javimo ti se na WhatsApp kad imamo slobodno mjesto.")), /*#__PURE__*/React.createElement(Reveal, {
    delay: 90
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-price-card"
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 190,
    rotate: -14,
    opacity: 0.07,
    color: "var(--cream-100)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      right: -44,
      bottom: -52
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-price-head"
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-family-card.svg',
    alt: "Boni Family",
    className: "bh-price-logo",
    loading: "lazy",
    decoding: "async"
  }), /*#__PURE__*/React.createElement("span", {
    className: "bh-price-tag"
  }, ACCESS_LABEL)), /*#__PURE__*/React.createElement("ul", {
    className: "bh-join-list"
  }, JOIN_INSIDE.map(t => /*#__PURE__*/React.createElement("li", {
    key: t
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 18,
    opacity: 1,
    color: "var(--teal-400)"
  }), t))), /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "lg",
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: {
      background: "var(--white)",
      color: "var(--ink-900)",
      border: "none",
      width: "100%",
      justifyContent: "center"
    },
    onClick: () => window.openBoniWizard && window.openBoniWizard()
  }, "Prijavi se")))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-900)",
    variant: 0
  }));
}
window.JoinClose = JoinClose;
function Footer() {
  // Every footer link must go somewhere real. Pages that do not exist yet (O nama, Blog,
  // Česta pitanja, Dostava, Povrat, Uvjeti) stay OUT until they are written; a dead link
  // on the page ad traffic lands on reads as a fake shop (Blueprint flagged it 2026-10-20).
  // "Postavke kolačića" reopens Shopify's consent banner so a choice can be changed (GDPR).
  // 2026-10-25 (beta): "Hrana" (no food in the beta) and "Piši nam na WhatsApp"
  // (Boni is application-only now; the WhatsApp page explains the way in) removed.
  const cols = [["Boni", [["Boni Family", "/"], ["Boni na WhatsAppu", "/pages/whatsapp"]]], ["Podrška", [["Kontakt", "mailto:info@boni.hr"]]], ["Pravno", [["Pravila privatnosti", "/pages/privatnost"], ["Uvjeti korištenja", "/pages/uvjeti"], ["Politika kolačića", "/pages/kolacici"], ["Postavke kolačića", "#kolacici", () => window.boniCookieSettings && window.boniCookieSettings()]]]];
  return /*#__PURE__*/React.createElement("footer", {
    style: {
      background: "var(--ink-900)",
      color: "var(--cream-100)",
      position: "relative",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 24,
      justifyContent: "center",
      padding: "20px 0 0"
    }
  }, [0, 1, 2, 3, 4, 5, 6].map(k => /*#__PURE__*/React.createElement(PawPrint, {
    key: k,
    size: 16,
    color: "var(--ink-500)",
    rotate: k % 2 ? 12 : -8,
    style: {
      opacity: 0.55
    }
  }))), /*#__PURE__*/React.createElement(PawMark, {
    size: 220,
    rotate: -12,
    opacity: 0.06,
    color: "var(--cream-100)",
    className: "bh-doodle",
    style: {
      position: "absolute",
      right: -44,
      bottom: -56
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-footer-grid",
    style: {
      padding: "var(--space-7) var(--gutter) var(--space-6)",
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-footer-brand"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      background: "var(--cream-100)",
      display: "inline-block",
      padding: "9px 15px",
      borderRadius: "var(--radius-md)"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + 'boni-logo-violet.png',
    alt: "Boni",
    loading: "lazy",
    decoding: "async",
    style: {
      height: 28,
      display: "block"
    }
  })), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "var(--font-text)",
      fontSize: 14.5,
      color: "var(--cream-300)",
      marginTop: 14,
      maxWidth: 260,
      lineHeight: 1.6
    }
  }, "Prvo mjesto za pet parente u Hrvatskoj.")), cols.map(([h, links]) => /*#__PURE__*/React.createElement("div", {
    key: h
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "var(--font-display)",
      fontWeight: 700,
      fontSize: 16,
      color: "var(--cream-300)",
      marginBottom: 12
    }
  }, h), links.map(([l, href, onClick]) => /*#__PURE__*/React.createElement("a", {
    key: l,
    href: href,
    onClick: onClick ? e => {
      e.preventDefault();
      onClick();
    } : undefined,
    style: {
      display: "block",
      fontFamily: "var(--font-text)",
      color: "var(--cream-200)",
      textDecoration: "none",
      fontSize: 14.5,
      fontWeight: 600,
      padding: "5px 0",
      cursor: "pointer"
    }
  }, l))))), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap bh-footer-bar",
    style: {
      borderTop: "1px solid rgba(255,255,255,0.12)",
      padding: "16px var(--gutter)",
      fontFamily: "var(--font-text)",
      fontSize: 13,
      color: "var(--cream-300)",
      fontWeight: 600
    }
  }, /*#__PURE__*/React.createElement("span", null, "© 2026 Boni. Sva prava pridržana."), /*#__PURE__*/React.createElement("span", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap"
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 15,
    opacity: 1,
    color: "var(--yellow-400)"
  }), /*#__PURE__*/React.createElement("span", null, "Napravljeno u Hrvatskoj, za pet parente."))));
}
window.Footer = Footer;
// ── Boni · catalog page (live) ─────────────────────────────────────────────
// Claude Design "Sva hrana" look, wired to REAL Shopify products injected by
// Liquid as window.BONI_PRODUCTS + window.BONI_SHOP. Client-side filtering.
// All filters are multi-select checkbox rows; price is a dual-handle slider.

const SHOP_BRANDS = [{
  name: 'Royal Canin',
  slug: 'royal-canin'
}, {
  name: "Hill's",
  slug: 'hills'
}, {
  name: 'Orijen',
  slug: 'orijen'
}, {
  name: 'Acana',
  slug: 'acana'
}, {
  name: 'Brit',
  slug: 'brit'
}, {
  name: 'Arquivet',
  slug: 'arquivet'
}, {
  name: 'K9',
  slug: 'k9'
}, {
  name: 'Purina',
  slug: 'purina'
}];
const SHOP_BRAND_BG = {
  'royal-canin': '#f6f1e7',
  hills: '#053b53',
  orijen: '#ffffff',
  acana: '#f5edd6',
  brit: '#15489d',
  arquivet: '#fafaf5',
  k9: '#f7f4e8',
  purina: '#ffffff'
};
const SHOP_TONES = ['coral', 'sky', 'teal', 'yellow', 'berry'];
// per-brand single theme colour (used for the banner tint + all product cards on that brand page)
const BRAND_THEME = {
  'royal-canin': 'sky',
  hills: 'teal',
  orijen: 'leaf',
  acana: 'coral',
  brit: 'berry',
  arquivet: 'yellow',
  k9: 'sky',
  purina: 'coral'
};
const BRAND_BADGES = {
  'royal-canin': ['Veterinarski', 'Po pasmini'],
  hills: ['Klinički', 'Zdrava probava'],
  orijen: ['Bez žitarica', '85% mesa'],
  acana: ['Svježe meso', 'Sa farme'],
  brit: ['Europska kvaliteta', 'Povoljno'],
  arquivet: ['Mediteranski', 'Prirodno'],
  k9: ['Sportski', 'Visoka energija'],
  purina: ['Znanstveno', 'Za sve faze']
};
const LIFE = [{
  id: 'Štene',
  label: 'Štene',
  sub: '0-12 mjeseci'
}, {
  id: 'Odrasli pas',
  label: 'Odrasli pas',
  sub: '1-7 godina'
}, {
  id: 'Senior',
  label: 'Senior',
  sub: '7+ godina'
}];
const SIZES = [{
  id: 'Mini',
  label: 'Mini',
  sub: 'do 5 kg'
}, {
  id: 'Mala',
  label: 'Mala',
  sub: '5-10 kg'
}, {
  id: 'Srednja',
  label: 'Srednja',
  sub: '10-25 kg'
}, {
  id: 'Velika',
  label: 'Velika',
  sub: '25+ kg'
}];
const TYPES = [{
  id: 'Suha hrana',
  label: 'Suha hrana'
}, {
  id: 'Mokra hrana',
  label: 'Mokra hrana'
}, {
  id: 'Poslastice',
  label: 'Poslastice'
}];
const WRAP_S = {
  maxWidth: 'var(--container)',
  margin: '0 auto',
  padding: '0 var(--gutter)'
};
function arr(v) {
  return Array.isArray(v) ? v : v == null || v === '' ? [] : [v];
}
function uniq(list) {
  return Array.from(new Set(list));
}

// consistent multi-select checkbox row (label + optional sub + circle check)
function CheckRow({
  label,
  sub,
  active,
  onClick
}) {
  const [hover, setHover] = React.useState(false);
  return /*#__PURE__*/React.createElement("button", {
    onClick: onClick,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    style: {
      width: '100%',
      cursor: 'pointer',
      font: 'inherit',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background: 'var(--cream-50)',
      border: active ? '1px solid var(--ink-400)' : '1px solid var(--hair)',
      borderRadius: 'var(--radius-md)',
      padding: '11px 14px',
      marginBottom: 8,
      boxShadow: hover ? 'var(--shadow-xs)' : 'none',
      transition: 'border-color var(--dur-fast)'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1,
      textAlign: 'left'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'block',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 14.5,
      color: 'var(--ink-900)',
      lineHeight: 1.2
    }
  }, label), sub && /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'block',
      marginTop: 2,
      fontFamily: 'var(--font-text)',
      fontWeight: 600,
      fontSize: 12,
      color: 'var(--ink-500)'
    }
  }, sub)), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 'none',
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: active ? 'none' : '2px solid var(--ink-300)',
      background: active ? 'var(--ink-900)' : 'transparent',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: 12,
      fontWeight: 900
    }
  }, active ? '✓' : ''));
}

// dual-handle price slider (drag both ends)
function PriceRange({
  lo,
  hi,
  min,
  max,
  setMin,
  setMax
}) {
  const span = hi - lo || 1;
  const pct = v => (v - lo) / span * 100;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 14,
      fontFamily: 'var(--font-display)',
      fontWeight: 800,
      color: 'var(--ink-900)',
      fontSize: 14
    }
  }, /*#__PURE__*/React.createElement("span", null, min, " €"), /*#__PURE__*/React.createElement("span", null, max, " €")), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      height: 22
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      top: 9,
      left: 0,
      right: 0,
      height: 4,
      borderRadius: 2,
      background: 'var(--ink-300)'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      top: 9,
      height: 4,
      borderRadius: 2,
      background: 'var(--ink-900)',
      left: pct(min) + '%',
      right: 100 - pct(max) + '%'
    }
  }), /*#__PURE__*/React.createElement("input", {
    className: "boni-range",
    type: "range",
    min: lo,
    max: hi,
    value: min,
    onChange: e => setMin(Math.min(+e.target.value, max)),
    "aria-label": "Minimalna cijena"
  }), /*#__PURE__*/React.createElement("input", {
    className: "boni-range",
    type: "range",
    min: lo,
    max: hi,
    value: max,
    onChange: e => setMax(Math.max(+e.target.value, min)),
    "aria-label": "Maksimalna cijena"
  })));
}

// ── product card ──────────────────────────────────────────────────────────
// Edgard & Cooper shape (the reference the brief named): a tinted rounded tile,
// then the name and price sitting DIRECTLY on the page background. No white
// card box, no border, no card shadow, no tag pills and no CTA button.
//
// Why the old one had to go:
//  · it stacked four blocks under the image (title, up to 3 tag pills, a
//    two-line price, a full-width button) and at the 2-up mobile width every
//    pill wrapped onto its own line, so the card ran ~440px tall for ~170px of
//    width. E&C carries two lines of text. Ours now does too.
//  · the brand chip was absolutely positioned INSIDE the image panel and the
//    <img> came after it in DOM order with no z-index, so THE IMAGE PAINTED
//    OVER THE BRAND. The brand is now a text line below the tile, where nothing
//    can cover it, and the one remaining badge carries an explicit z-index.
//  · the bowl pill looked like add-to-cart but only navigated to the product
//    page. Dropping it removes that lie: the whole card is already the link.
// The badge reads p.life[0], NOT tags[0] — tags is a derived, order-dependent
// array, while life is the real `boni.zivotna_faza` metafield.
function ShopCard({
  p,
  i,
  forceTone,
  showBrand = true
}) {
  const [hover, setHover] = React.useState(false);
  const tone = forceTone || SHOP_TONES[i % SHOP_TONES.length];
  const badge = p.life && p.life[0] || '';
  return /*#__PURE__*/React.createElement("a", {
    href: p.url,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    style: {
      display: 'flex',
      flexDirection: 'column',
      textDecoration: 'none'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      aspectRatio: '1 / 1',
      background: `var(--${tone}-100)`,
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      transform: `translateY(${hover ? -4 : 0}px)`,
      transition: 'transform var(--dur-base) var(--ease-out)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 150,
    color: `var(--${tone}-400)`,
    opacity: 0.18,
    style: {
      position: 'absolute',
      right: -28,
      bottom: -24
    }
  }), p.image ? /*#__PURE__*/React.createElement("img", {
    src: p.image,
    alt: p.title,
    loading: "lazy",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      padding: '11% 14%',
      transform: `scale(${hover ? 1.04 : 1})`,
      transition: 'transform var(--dur-base) var(--ease-out)'
    }
  }) : /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-display)',
      fontWeight: 800,
      fontSize: 30,
      color: `var(--${tone}-600)`,
      opacity: 0.5
    }
  }, p.vendor), badge && /*#__PURE__*/React.createElement("span", {
    className: "bs-card-badge",
    style: {
      position: 'absolute',
      top: 12,
      right: 12,
      zIndex: 2,
      background: '#fff',
      color: 'var(--ink-900)',
      borderRadius: 'var(--radius-pill)',
      padding: '5px 12px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 11.5
    }
  }, badge)), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '12px 3px 0'
    }
  }, showBrand && /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'block',
      marginBottom: 5,
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: 'var(--ink-700)'
    }
  }, p.vendor), /*#__PURE__*/React.createElement("h3", {
    className: "bs-card-t",
    style: {
      margin: 0,
      minHeight: '2.4em',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 16.5,
      lineHeight: 1.2,
      color: 'var(--ink-900)',
      textDecoration: hover ? 'underline' : 'none',
      textUnderlineOffset: 3
    }
  }, p.title), /*#__PURE__*/React.createElement("span", {
    className: "bs-card-p",
    style: {
      display: 'block',
      marginTop: 6,
      fontFamily: 'var(--font-text)',
      fontWeight: 700,
      fontSize: 15,
      color: 'var(--ink-700)'
    }
  }, p.varies ? 'od ' : '', p.price)));
}

// the "Ne znaš što odabrati?" wizard card (original navy design) — top of rail
// ── "we don't stock your food yet" ask ────────────────────────
// Replaces the old club card (whose CTA linked to /#kako-radi, i.e. its one job
// was to send a buying visitor off the catalog). Sits directly under the brand
// tiles, because failing to find your brand in that grid IS the moment to ask.
//
// Deliberately QUIET: blush surface, one small violet pill. It is a fallback,
// not the page's offer, so the products stay the loudest thing on the page and
// we don't burn a violet panel on a utility.
//
// TRANSPORT — a real <form> POST to Shopify's /contact, NOT fetch. Shopify's bot
// protection answers automated POSTs with a "Verifying your connection" 403
// (verified 2026-08-25, curl AND headless chromium), so a fetch can silently
// drop every request. A top-level form submit is what every Shopify theme does
// and cannot be challenged. Cost is a page reload; we come back on
// ?zahtjev=ok|mail and render the receipt in the card's place.
//
// Email is optional in the UI, but /contact requires a valid address, so a
// blank one posts as ASK_FALLBACK_EMAIL and the body says so in words. The
// request still reaches the inbox either way, which is the point: the demand
// list is worth more than the reminder.
const ASK_FALLBACK_EMAIL = 'bez-emaila@boni.hr';
function AskModal({
  onClose,
  idp = 'ask'
}) {
  const [food, setFood] = React.useState('');
  const [email, setEmail] = React.useState('');
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  const clean = food.trim();
  const mail = email.trim();
  const back = window.location.pathname + '?zahtjev=' + (mail ? 'mail' : 'ok');
  const body = mail ? 'Zahtjev za hranu iz kataloga: ' + clean + '\nJaviti na: ' + mail : 'Zahtjev za hranu iz kataloga: ' + clean + '\n(bez emaila, samo za popis što dodati)';
  const L = {
    display: 'block',
    marginBottom: 6,
    fontFamily: 'var(--font-text)',
    fontWeight: 800,
    fontSize: 12.5,
    letterSpacing: '.02em',
    textTransform: 'uppercase',
    color: 'var(--ink-600)'
  };
  return /*#__PURE__*/React.createElement("div", {
    onClick: onClose,
    style: {
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      background: 'rgba(44,27,69,.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }
  }, /*#__PURE__*/React.createElement("div", {
    onClick: e => e.stopPropagation(),
    role: "dialog",
    "aria-modal": "true",
    style: {
      width: '100%',
      maxWidth: 430,
      background: 'var(--white)',
      borderRadius: 'var(--radius-lg)',
      padding: '26px 24px 24px',
      position: 'relative',
      boxShadow: '0 24px 60px rgba(44,27,69,.28)'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onClose,
    "aria-label": "Zatvori",
    style: {
      position: 'absolute',
      top: 12,
      right: 12,
      cursor: 'pointer',
      font: 'inherit',
      background: 'none',
      border: 'none',
      fontSize: 24,
      lineHeight: 1,
      color: 'var(--ink-500)',
      padding: 6
    }
  }, "×"), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '0 0 6px',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 21,
      color: 'var(--ink-900)',
      lineHeight: 1.2
    }
  }, "Koju hranu tražiš?"), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '0 0 20px',
      fontFamily: 'var(--font-text)',
      fontSize: 14.5,
      color: 'var(--ink-600)',
      lineHeight: 1.5
    }
  }, "Katalog nam raste. Ako nam kažeš što tvoj pas jede, znamo što dodati sljedeće."), /*#__PURE__*/React.createElement("form", {
    method: "post",
    action: "/contact"
  }, /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "form_type",
    value: "contact"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utf8",
    value: "✓"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "return_to",
    value: back
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "contact[email]",
    value: mail || ASK_FALLBACK_EMAIL
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "contact[body]",
    value: body
  }), /*#__PURE__*/React.createElement("label", {
    style: L,
    htmlFor: idp + "-food"
  }, "Hrana ili brend"), /*#__PURE__*/React.createElement("input", {
    id: idp + "-food",
    className: "bs-ask-in",
    name: "contact[Hrana]",
    value: food,
    onChange: e => setFood(e.target.value),
    required: true,
    autoFocus: true,
    placeholder: "npr. Purina Pro Plan Medium Adult"
  }), /*#__PURE__*/React.createElement("label", {
    style: {
      ...L,
      marginTop: 16
    },
    htmlFor: idp + "-mail"
  }, "Email"), /*#__PURE__*/React.createElement("input", {
    id: idp + "-mail",
    className: "bs-ask-in",
    type: "email",
    value: email,
    onChange: e => setEmail(e.target.value),
    placeholder: "ime@mail.com"
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '7px 0 0',
      fontFamily: 'var(--font-text)',
      fontSize: 12.5,
      color: 'var(--ink-500)',
      lineHeight: 1.45
    }
  }, "Nije obavezno. Samo da ti javimo kad je dodamo."), /*#__PURE__*/React.createElement("button", {
    type: "submit",
    style: {
      marginTop: 20,
      width: '100%',
      cursor: 'pointer',
      font: 'inherit',
      background: 'var(--brand)',
      color: '#fff',
      border: 'none',
      borderRadius: 'var(--radius-pill)',
      padding: '13px 20px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 15
    }
  }, "Pošalji"))));
}
function AskCard({
  idp = 'ask'
}) {
  const [open, setOpen] = React.useState(false);
  // read once, lazily: we strip the flag from the URL right after, and a later
  // re-render must not recompute this and flip the receipt back to the form.
  const [sent] = React.useState(() => {
    const q = window.location.search || '';
    return /[?&]zahtjev=mail/.test(q) ? 'mail' : /[?&]zahtjev=ok/.test(q) || /[?&]contact_posted=true/.test(q) ? 'ok' : '';
  });
  React.useEffect(() => {
    if (sent && window.history && window.history.replaceState) {
      // so a refresh or a shared link doesn't replay the receipt
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [sent]);
  if (sent) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 14,
        padding: '16px 18px',
        background: 'var(--yellow-50)',
        border: '1px solid var(--teal-200)',
        borderRadius: 'var(--radius-lg)'
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: 0,
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: 15.5,
        color: 'var(--ink-900)'
      }
    }, "Zapisali smo!"), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: '6px 0 0',
        fontFamily: 'var(--font-text)',
        fontSize: 13.5,
        color: 'var(--ink-700)',
        lineHeight: 1.5
      }
    }, sent === 'mail' ? 'Javit ćemo ti se na mail kad tu hranu dodamo u katalog.' : 'Sad znamo što nam sljedeće treba u katalogu. Hvala!'));
  }
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 14,
      padding: '16px 18px',
      background: 'var(--coral-50)',
      border: '1px solid var(--hair)',
      borderRadius: 'var(--radius-lg)',
      position: 'relative',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 78,
    color: "var(--coral-400)",
    opacity: 0.16,
    style: {
      position: 'absolute',
      right: -12,
      bottom: -14
    }
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: 0,
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 15.5,
      color: 'var(--ink-900)',
      lineHeight: 1.25,
      position: 'relative'
    }
  }, "Ne vidiš svoju hranu?"), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '6px 0 12px',
      fontFamily: 'var(--font-text)',
      fontWeight: 500,
      fontSize: 13,
      color: 'var(--ink-700)',
      lineHeight: 1.45,
      position: 'relative'
    }
  }, "Reci nam koju hranu tvoj pas jede. Javimo ti kad je dodamo u katalog."), /*#__PURE__*/React.createElement("button", {
    onClick: () => setOpen(true),
    style: {
      position: 'relative',
      cursor: 'pointer',
      font: 'inherit',
      background: 'var(--brand)',
      color: '#fff',
      border: 'none',
      borderRadius: 'var(--radius-pill)',
      padding: '9px 16px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 13.5,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7
    }
  }, "Predloži hranu ", /*#__PURE__*/React.createElement("span", null, "→")), open && /*#__PURE__*/React.createElement(AskModal, {
    idp: idp,
    onClose: () => setOpen(false)
  }));
}

// collapsible filter "bubble" — minimised by default
function FilterBubble({
  title,
  tone = 'sky',
  defaultOpen = false,
  children
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      background: 'var(--cream-200)',
      border: '1px solid var(--hair)',
      borderRadius: 'var(--radius-lg)',
      marginBottom: 10,
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setOpen(o => !o),
    style: {
      width: '100%',
      cursor: 'pointer',
      font: 'inherit',
      display: 'flex',
      alignItems: 'center',
      gap: 11,
      background: 'none',
      border: 'none',
      padding: '13px 16px'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: `var(--${tone}-400)`,
      flex: 'none'
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1,
      textAlign: 'left',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 14.5,
      color: 'var(--ink-900)'
    }
  }, title), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 'none',
      fontFamily: 'var(--font-text)',
      fontWeight: 700,
      fontSize: 20,
      color: 'var(--ink-500)',
      lineHeight: 1,
      width: 16,
      textAlign: 'center'
    }
  }, open ? '−' : '+')), open && /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '0 16px 14px'
    }
  }, children));
}

// brand tile — clean grid cell, no tilt
function BrandTile({
  b
}) {
  const [hover, setHover] = React.useState(false);
  return /*#__PURE__*/React.createElement("a", {
    href: '/collections/' + b.slug,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    style: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 9,
      textAlign: 'center',
      textDecoration: 'none',
      background: 'var(--surface-raised)',
      border: '1px solid var(--hair)',
      borderRadius: 'var(--radius-lg)',
      padding: '12px 10px',
      boxShadow: hover ? 'var(--shadow-md)' : 'none',
      transform: `translateY(${hover ? -3 : 0}px)`,
      transition: 'transform var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out)'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: '100%',
      height: 52,
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
      background: SHOP_BRAND_BG[b.slug],
      border: '1px solid var(--hair)',
      display: 'block',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: window.K + `brands-${b.slug}-logo.jpeg`,
    alt: b.name,
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      boxSizing: 'border-box',
      padding: '10%'
    }
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 13.5,
      color: 'var(--ink-900)'
    }
  }, b.name));
}

/* ── PlanGuide MOVED OUT of this React bundle (2026-08-28) ─────────────────────────
   The game-tutorial step popup now needs to show on the PDP too (KORAK 2), and the
   PDP is native Liquid — so ONE shared vanilla implementation lives in
   boni-shopify-theme/assets/boni-guide.js (styles stay in boni-store.css .bsg-*),
   loaded by theme.liquid on every page. Do NOT re-add a React copy here: two
   implementations of the same popup is exactly the drift this move avoids.        */

function Shop() {
  const CFG = window.BONI_SHOP || {};
  const brandTone = BRAND_THEME[CFG.handle] || 'sky';
  const badges = BRAND_BADGES[CFG.handle] || [];

  // ── Load the FULL catalog, not just Liquid's first 50 ──────────────────────
  // boni-shop-mount injects page 1 (≤50) inline for instant paint; any further
  // pages are fetched as JSON (the collection.boni-json view) and merged, so the
  // client-side filters below see EVERY product, not just the first page.
  const [extra, setExtra] = React.useState([]);
  const [loadingMore, setLoadingMore] = React.useState((window.BONI_PAGES || 1) > 1);
  React.useEffect(() => {
    const pages = window.BONI_PAGES || 1;
    if (pages > 1 && CFG.handle) {
      const reqs = [];
      for (let pg = 2; pg <= pages; pg++) {
        reqs.push(fetch('/collections/' + CFG.handle + '?view=boni-json&page=' + pg).then(r => r.json()).then(d => d && d.products || []).catch(() => []));
      }
      Promise.all(reqs).then(chunks => {
        setExtra([].concat.apply([], chunks));
        setLoadingMore(false);
      });
    }
  }, []);
  const seen = {};
  const ALL = (window.BONI_PRODUCTS || []).concat(extra).filter(p => {
    if (seen[p.id]) return false;
    seen[p.id] = 1;
    return true;
  }).map(p => ({
    ...p,
    life: arr(p.life),
    size: arr(p.size),
    posebne: arr(p.posebne),
    protein: arr(p.protein),
    tags: uniq([].concat(arr(p.life)[0] || [], p.type || [], arr(p.posebne)[0] || []).filter(Boolean))
  }));
  const nums = ALL.map(p => p.priceNum).filter(n => typeof n === 'number');
  const LO = nums.length ? Math.floor(Math.min.apply(null, nums)) : 0;
  const HI = nums.length ? Math.ceil(Math.max.apply(null, nums)) : 100;

  // every filter is multi-select (array of selected values)
  const [life, setLife] = React.useState([]);
  const [size, setSize] = React.useState([]);
  const [brand, setBrand] = React.useState([]);
  const [type, setType] = React.useState([]);
  const [need, setNeed] = React.useState([]);
  const [protein, setProtein] = React.useState([]);
  const [pmin, setPmin] = React.useState(LO);
  const [pmax, setPmax] = React.useState(HI);
  const [touchedPrice, setTouchedPrice] = React.useState(false);
  // keep the price bounds correct as more products stream in (until the user drags)
  React.useEffect(() => {
    if (!touchedPrice) {
      setPmin(LO);
      setPmax(HI);
    }
  }, [LO, HI]);
  const tog = (list, val, set) => set(list.indexOf(val) > -1 ? list.filter(x => x !== val) : list.concat(val));
  const POSEBNE = uniq(ALL.flatMap(p => p.posebne));
  const PROTEIN = uniq(ALL.flatMap(p => p.protein));
  const priceActive = pmin > LO || pmax < HI;
  const shown = ALL.filter(p => (!life.length || life.some(v => p.life.indexOf(v) > -1 || p.life.indexOf('Sve faze') > -1)) && (!size.length || size.some(v => p.size.indexOf(v) > -1 || p.size.indexOf('Sve veličine') > -1)) && (!brand.length || brand.indexOf(p.vendor) > -1) && (!type.length || type.indexOf(p.type) > -1) && (!need.length || need.some(v => p.posebne.indexOf(v) > -1)) && (!protein.length || protein.some(v => p.protein.indexOf(v) > -1)) && (typeof p.priceNum !== 'number' || p.priceNum >= pmin && p.priceNum <= pmax));
  const anyFilter = life.length || size.length || brand.length || type.length || need.length || protein.length || priceActive;
  const reset = () => {
    setLife([]);
    setSize([]);
    setBrand([]);
    setType([]);
    setNeed([]);
    setProtein([]);
    setPmin(LO);
    setPmax(HI);
    setTouchedPrice(false);
  };
  const nFilters = life.length + size.length + brand.length + type.length + need.length + protein.length + (priceActive ? 1 : 0);

  // MOBILE: the rail becomes a full-screen filter sheet behind a "Filtriraj"
  // button, so products are visible immediately instead of ~1700px down.
  // Desktop is untouched — the same markup, CSS does the whole switch, so there
  // is no JS breakpoint or resize listener to get out of sync.
  const [sheet, setSheet] = React.useState(false);
  React.useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = e => {
      if (e.key === 'Escape') setSheet(false);
    };
    window.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', esc);
    };
  }, [sheet]);
  return /*#__PURE__*/React.createElement("main", {
    style: {
      background: 'var(--cream-100)',
      paddingBottom: 'var(--space-10)'
    }
  }, CFG.isBrand && /*#__PURE__*/React.createElement("section", {
    style: {
      position: 'relative',
      width: '100%',
      height: 'clamp(280px, 34vw, 400px)',
      overflow: 'hidden',
      background: `var(--${brandTone}-100)`,
      marginBottom: 'var(--space-6)'
    }
  }, CFG.banner && /*#__PURE__*/React.createElement("img", {
    src: CFG.banner,
    alt: CFG.title,
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      background: `var(--${brandTone}-400)`,
      opacity: 0.18,
      mixBlendMode: 'multiply'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, rgba(15,53,87,.82) 0%, rgba(15,53,87,.5) 40%, rgba(15,53,87,.05) 74%)'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      height: '100%',
      ...WRAP_S,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      paddingBottom: 28
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      marginBottom: 12
    }
  }, CFG.image && /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 'none',
      width: 64,
      height: 64,
      borderRadius: 'var(--radius-md)',
      background: '#fff',
      boxShadow: 'var(--shadow-md)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: CFG.image,
    alt: CFG.title,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      padding: 9,
      boxSizing: 'border-box'
    }
  })), /*#__PURE__*/React.createElement("h1", {
    style: {
      margin: 0,
      fontFamily: 'var(--font-display)',
      fontWeight: 800,
      fontSize: 'clamp(2rem, 4.4vw, 3.2rem)',
      lineHeight: 1.02,
      letterSpacing: '-.02em',
      color: '#fff'
    }
  }, CFG.title)), CFG.description && /*#__PURE__*/React.createElement("p", {
    style: {
      margin: 0,
      fontFamily: 'var(--font-text)',
      fontWeight: 500,
      fontSize: 'clamp(.95rem, 1.4vw, 1.1rem)',
      lineHeight: 1.5,
      color: 'rgba(255,255,255,.92)',
      maxWidth: 540
    }
  }, CFG.description), badges.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 14
    }
  }, badges.map(t => /*#__PURE__*/React.createElement("span", {
    key: t,
    style: {
      background: 'rgba(255,255,255,.16)',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255,255,255,.35)',
      color: '#fff',
      borderRadius: 'var(--radius-pill)',
      padding: '6px 14px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 12.5
    }
  }, t))))), !CFG.isBrand && /*#__PURE__*/React.createElement("section", {
    style: {
      ...WRAP_S,
      paddingTop: 'var(--space-6)',
      marginBottom: 'var(--space-6)'
    }
  }, /*#__PURE__*/React.createElement("h2", {
    style: {
      margin: '0 0 14px',
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 18,
      color: 'var(--ink-900)'
    }
  }, "Kupuj po brendu"), /*#__PURE__*/React.createElement("div", {
    className: "bs-brands",
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))',
      gap: 12
    }
  }, SHOP_BRANDS.map(b => /*#__PURE__*/React.createElement(BrandTile, {
    key: b.slug,
    b: b
  })))), /*#__PURE__*/React.createElement("section", {
    style: {
      ...WRAP_S,
      display: 'grid',
      gridTemplateColumns: '264px 1fr',
      gap: 40,
      alignItems: 'start'
    },
    className: "boni-shop-body"
  }, /*#__PURE__*/React.createElement("aside", {
    className: 'boni-shop-rail' + (sheet ? ' is-open' : ''),
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bs-sheet-head"
  }, /*#__PURE__*/React.createElement("button", {
    className: "bs-sheet-back",
    onClick: () => setSheet(false),
    "aria-label": "Zatvori filtre"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: "18",
    height: "18",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M15 5l-7 7 7 7"
  }))), /*#__PURE__*/React.createElement("span", {
    className: "bs-sheet-title"
  }, "Filtriraj"), nFilters > 0 ? /*#__PURE__*/React.createElement("button", {
    className: "bs-sheet-reset",
    onClick: reset
  }, "Poništi") : /*#__PURE__*/React.createElement("span", {
    style: {
      width: 52
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "bs-sheet-scroll"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bs-rail-ask"
  }, /*#__PURE__*/React.createElement(AskCard, {
    idp: "rail"
  })), anyFilter && /*#__PURE__*/React.createElement("div", {
    className: "bs-rail-reset",
    style: {
      textAlign: 'right',
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: reset,
    style: {
      font: 'inherit',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      color: 'var(--coral-600)',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 13
    }
  }, "Poništi filtre")), /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Životna faza",
    tone: "coral",
    defaultOpen: true
  }, LIFE.map(l => /*#__PURE__*/React.createElement(CheckRow, {
    key: l.id,
    label: l.label,
    sub: l.sub,
    active: life.indexOf(l.id) > -1,
    onClick: () => tog(life, l.id, setLife)
  }))), /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Veličina pasmine",
    tone: "sky",
    defaultOpen: true
  }, SIZES.map(s => /*#__PURE__*/React.createElement(CheckRow, {
    key: s.id,
    label: s.label,
    sub: s.sub,
    active: size.indexOf(s.id) > -1,
    onClick: () => tog(size, s.id, setSize)
  }))), /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Brend",
    tone: "teal"
  }, SHOP_BRANDS.map(b => /*#__PURE__*/React.createElement(CheckRow, {
    key: b.slug,
    label: b.name,
    active: brand.indexOf(b.name) > -1,
    onClick: () => tog(brand, b.name, setBrand)
  }))), /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Tip hrane",
    tone: "teal"
  }, TYPES.map(t => /*#__PURE__*/React.createElement(CheckRow, {
    key: t.id,
    label: t.label,
    active: type.indexOf(t.id) > -1,
    onClick: () => tog(type, t.id, setType)
  }))), POSEBNE.length > 0 && /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Posebne potrebe",
    tone: "coral"
  }, POSEBNE.map(n => /*#__PURE__*/React.createElement(CheckRow, {
    key: n,
    label: n,
    active: need.indexOf(n) > -1,
    onClick: () => tog(need, n, setNeed)
  }))), PROTEIN.length > 0 && /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Glavni protein",
    tone: "yellow"
  }, PROTEIN.map(n => /*#__PURE__*/React.createElement(CheckRow, {
    key: n,
    label: n,
    active: protein.indexOf(n) > -1,
    onClick: () => tog(protein, n, setProtein)
  }))), /*#__PURE__*/React.createElement(FilterBubble, {
    title: "Cijena",
    tone: "sky"
  }, /*#__PURE__*/React.createElement(PriceRange, {
    lo: LO,
    hi: HI,
    min: pmin,
    max: pmax,
    setMin: v => {
      setTouchedPrice(true);
      setPmin(v);
    },
    setMax: v => {
      setTouchedPrice(true);
      setPmax(v);
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "bs-sheet-foot"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => setSheet(false)
  }, "Prikaži ", shown.length, " ", shown.length === 1 ? 'proizvod' : 'proizvoda'))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "bs-shop-head",
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 22,
      flexWrap: 'wrap',
      gap: 14
    }
  }, /*#__PURE__*/React.createElement("button", {
    className: "bs-filter-btn",
    onClick: () => setSheet(true)
  }, /*#__PURE__*/React.createElement("span", null, "Filtriraj"), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: "16",
    height: "16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4 7h11M18 7h2M4 17h5M12 17h8"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "16.5",
    cy: "7",
    r: "2.2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "10.5",
    cy: "17",
    r: "2.2"
  })), nFilters > 0 && /*#__PURE__*/React.createElement("span", {
    className: "bs-filter-n"
  }, nFilters)), /*#__PURE__*/React.createElement("span", {
    className: "bs-shop-count",
    style: {
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 18,
      color: 'var(--ink-900)'
    }
  }, shown.length, " ", shown.length === 1 ? 'recept' : 'recepata', " za sretne trbuščiće", loadingMore && /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-text)',
      fontWeight: 600,
      fontSize: 13,
      color: 'var(--ink-500)',
      marginLeft: 10
    }
  }, "učitavam jelovnik…"))), shown.length > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "boni-shop-grid",
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))',
      gap: 24
    }
  }, shown.map((p, i) => /*#__PURE__*/React.createElement(ShopCard, {
    key: p.id,
    p: p,
    i: i,
    forceTone: CFG.isBrand ? brandTone : undefined,
    showBrand: !CFG.isBrand
  }))) : /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: 'center',
      padding: '64px 24px',
      background: 'var(--surface-raised)',
      border: '1px dashed var(--cream-300)',
      borderRadius: 'var(--radius-xl)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 64,
    color: "var(--coral-400)",
    opacity: 0.5,
    style: {
      margin: '0 auto 16px'
    }
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: 0,
      fontFamily: 'var(--font-display)',
      fontWeight: 700,
      fontSize: 20,
      color: 'var(--ink-900)'
    }
  }, "Nema obroka za te filtre"), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: '8px 0 18px',
      fontFamily: 'var(--font-text)',
      fontSize: 15,
      color: 'var(--ink-600)'
    }
  }, "Probaj ukloniti neki filter."), /*#__PURE__*/React.createElement("button", {
    onClick: reset,
    style: {
      cursor: 'pointer',
      font: 'inherit',
      background: 'var(--ink-900)',
      color: '#fff',
      border: 'none',
      borderRadius: 'var(--radius-pill)',
      padding: '11px 22px',
      fontFamily: 'var(--font-text)',
      fontWeight: 800,
      fontSize: 14
    }
  }, "Poništi filtre")), /*#__PURE__*/React.createElement("div", {
    className: "bs-grid-ask"
  }, /*#__PURE__*/React.createElement(AskCard, {
    idp: "grid"
  })))), sheet && /*#__PURE__*/React.createElement("div", {
    className: "bs-sheet-scrim",
    onClick: () => setSheet(false)
  }));
}
window.Shop = Shop;
// ── /pages/whatsapp — what Boni asistentica is, in the BETA ───────────────
// REWRITTEN 2026-10-25 (Maks + Ante launch list): the page used to sell the
// food-delivery assistant. The beta (rani pristup) is Journal-only and free,
// so the page now sells the conversation that turns into a Journal.
//
// SPINE — read the headings top to bottom and they are the whole argument:
//   1 Fotke tvog psa napokon dobivaju priču.               what it is
//   2 Za to smo napravili Boni asistenticu na WhatsAppu.   why she exists
//   3 Boni ti se javi kad te pustimo unutra.               how it works
//     Od tada piše priču tvog psa.
//   4 three showcases in the owner's own words            proof
//   5 Upoznaj Boni asistenticu.                            the ask
//
// Every section is a different KIND of statement (what / why / how / proof /
// ask), never a different fact about the same thing. Section 3 TELLS and shows
// no bubbles; every real message sits in the showcase band. Each showcase lands
// one job: a photo becomes a page, an old story lands on its real date, habits
// and people become the line at the top of the Journal.
//
// THE CTA IS THE APPLICATION, NOT THE CHAT. The beta is application-only, so
// the primary action opens the wizard; messaging Boni directly is a quiet
// secondary link for people who are already in.
// Layout classes are bh-wap-* at the END of boni-home.css. Chrome (Header /
// Announce / Footer) is mounted by theme.liquid on every non-index page.

const WA_NUMBER = '385976128956';
const WA_DISPLAY = '+385 97 612 8956';
// The prefilled text is load-bearing: it removes the blank-compose-box freeze
// for someone who does not know what to say, and gives the cold-arrival
// playbook a predictable opening token to route on.
const WA_LINK = 'https://wa.me/' + WA_NUMBER + '?text=' + encodeURIComponent('Bok Boni!');
const WA_BTN = {
  background: 'var(--brand)',
  color: 'var(--white)',
  border: 'none'
};

// The hero mockup has to DEMONSTRATE the hero's own claim, so it is exactly
// three bubbles: she writes first with the date and an open question, one reply
// changes it, done. Any longer and "jednom porukom" stops being literal.
// It stays distinct from showcase 1, which is the same flow with a reason given
// and Boni volunteering the fix before being asked — that is showcase 1's point,
// not this one's. Both dates are real Thursdays, a week apart.
const WA_HERO_MSGS = [{
  side: 'out',
  img: window.K + 'journal-park-illu-v2.webp',
  text: 'Prvi put na Bundeku, odmah je našao nečiju lopticu 😂',
  time: '9:41'
}, {
  side: 'in',
  text: 'Tuđa loptica je uvijek bolja 😄 Je li je vratio?',
  time: '9:41'
}, {
  side: 'out',
  text: 'Ni slučajno',
  time: '9:42'
}, {
  side: 'in',
  text: 'Zapisano 📖 Hugo ima novu stranicu u Journalu.',
  time: '9:42'
}];

// Section 3 — the mechanism, told in three steps and NOT shown. Step 3 is the
// load-bearing one: without it the page reads as "a place where you configure
// your order", when the point is that she is watching it for you. So it names
// what she actually tracks (bag size, portion, dates) and that SHE speaks first.
// Icons reuse the homepage's "Kako radi" doodles with THE SAME meanings — match
// = choosing food, chat = WhatsApp — which is consistency, not the S87 collision
// (that rule is about one icon meaning two different things). 'clock' is new,
// added to Icons.jsx for "she keeps count of the days"; deliberately not an eye,
// which would read as surveillance on an AI product.
const WA_STEPS = [{
  icon: 'match',
  color: 'var(--coral-400)',
  bg: 'var(--coral-50)',
  t: 'Prijaviš se za rani pristup.',
  s: 'Ispričaš nam nešto o sebi i svom psu. Prijave čitamo jednu po jednu.'
}, {
  icon: 'chat',
  color: 'var(--teal-600)',
  bg: 'var(--teal-100)',
  t: 'Boni ti se javi na WhatsApp.',
  s: 'Već zna kako se tvoj pas zove, pa se ne moraš predstavljati. Za početak je dovoljna jedna fotka.'
}, {
  icon: 'clock',
  color: 'var(--brand)',
  bg: 'var(--ink-50)',
  t: 'Ona piše njegov Journal.',
  s: 'Od svake fotke i priče nastaje nova stranica, poredana po danima. Link je samo tvoj.'
}];

// Section 4 — the showcases. Each thread is near-verbatim what the live system
// sends and does today: the 7-day nudge, the delay flow with Boni offering the
// fix before being asked, per-product quantity and frequency, pause, and the
// cancel path that asks about pause exactly once. Nothing here is aspirational.
const WA_SHOWCASES = [{
  q: '"Evo ga nakon kupanja."',
  s: 'Pošalješ fotku, a Boni primijeti ono što je na njoj važno i od toga napiše stranicu. Ne traži opis ni datum.',
  thread: [{
    side: 'out',
    text: 'Evo ga nakon kupanja 😅'
  }, {
    side: 'in',
    text: 'Ovaj pogled kaže da ti to neće zaboraviti 😄 Je li uvijek ovako uvrijeđen?'
  }, {
    side: 'out',
    text: 'Uvijek, pola sata me ne gleda.'
  }, {
    side: 'in',
    text: 'Zapisano 📖 Hugo ima novu stranicu u Journalu.'
  }]
}, {
  q: '"Kad smo ga uzeli, imao je dva mjeseca."',
  s: 'Stare priče idu na datum kad su se dogodile, a ne na dan kad ih ispričaš. Tako Journal s vremenom postaje cijeli njegov život.',
  thread: [{
    side: 'out',
    text: 'Kad smo ga uzeli, imao je dva mjeseca i cijeli put je spavao u kutiji za cipele.'
  }, {
    side: 'in',
    text: 'U kutiji za cipele 😍 Odakle ste ga doveli?'
  }, {
    side: 'out',
    text: 'Iz udruge u Velikoj Gorici, u ožujku 2022.'
  }, {
    side: 'in',
    text: 'Ta priča sad stoji na samom početku njegovog Journala 📖'
  }]
}, {
  q: '"Večernja šetnja ide samo s Anom."',
  s: 'Boni pamti navike i ljude oko tvog psa. Iz toga nastaje rečenica na vrhu Journala koja kaže tko je on.',
  thread: [{
    side: 'out',
    text: 'Večernja šetnja ide samo s Anom, sa mnom neće ni na vrata.'
  }, {
    side: 'in',
    text: 'Ima on svoju osobu za večer 😄 Što radi kad Ana kasni?'
  }, {
    side: 'out',
    text: 'Sjedi kod vrata i uzdiše.'
  }, {
    side: 'in',
    text: 'Na vrhu Journala sad piše: „Večer pripada Ani, a čekanje vratima.“'
  }]
}];

// apply = open the application wizard (the beta's primary action); otherwise
// the button opens the WhatsApp chat.
function WaCta({
  label,
  size = 'lg',
  onDark = false,
  apply = false
}) {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  return /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: size,
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: onDark ? {
      background: 'var(--white)',
      color: 'var(--ink-900)',
      border: 'none'
    } : WA_BTN,
    onClick: () => {
      if (apply) {
        if (window.openBoniWizard) window.openBoniWizard();
      } else {
        window.location.href = WA_LINK;
      }
    }
  }, label);
}

// A chat scrap on the real wallpaper. Shared by every showcase; bubble geometry
// and colours are the WhatsApp ones, same as inside the phone.
function WaThread({
  msgs
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-x bh-wap-x--thread"
  }, msgs.map((m, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    className: 'bh-wap-b bh-wap-b--' + m.side
  }, m.text)));
}

// 1 · HERO — what this is, in one line, next to the thing itself. The phone
// TUCKS under the wave exactly like the homepage's (full overlap + empty screen
// padding at the bottom); see .bh-wap-phone for the mechanic. No chip and no
// printed number here: the chip only restated the page's own title, and the
// number is one tap away in the button. Wave variant 3 stays — of the four it
// has the highest fill at the phone's x.
function WaHero() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--coral-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 92,
      left: '5%'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-hero"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "bh-wap-hero-copy"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "bh-wap-h1"
  }, 'Fotke tvog psa napokon dobivaju ', /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'relative',
      display: 'inline-block'
    }
  }, "priču", /*#__PURE__*/React.createElement(Squiggle, {
    color: "var(--coral-400)",
    width: "100%",
    height: 15,
    style: {
      position: 'absolute',
      left: 0,
      bottom: -7,
      width: '100%'
    }
  })), "."), /*#__PURE__*/React.createElement("p", {
    className: "bh-wap-lead"
  }, "Pošalješ Boni fotku ili ispričaš nešto o svom psu, a ona od toga piše Journal, osobni dnevnik samo o njemu. Sve na WhatsAppu, bez aplikacije i bez registracije."), /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-cta"
  }, /*#__PURE__*/React.createElement(WaCta, {
    label: "Prijavi se",
    apply: true
  }))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 110,
    className: "bh-wap-phone"
  }, /*#__PURE__*/React.createElement(PhoneChat, {
    msgs: WA_HERO_MSGS,
    clock: "9:42"
  })))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--coral-50)",
    variant: 3
  }));
}

// 2 · WHY SHE EXISTS, and WHO she is — the one place on the page where we speak
// as ourselves, which is also what stops it echoing the hero: the hero is about
// what YOU do, this is about what we built and what for. It carries the villain
// (running out), because the spine names it nowhere else. The card beside it is
// her introduction as an OBJECT rather than a paragraph — her actual WhatsApp
// Business profile, recreated, so the reader meets her instead of reading about
// her and recognises the screen when they get there.
function WaWhy() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--tight bh-section--wave",
    style: {
      background: 'var(--coral-50)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 92,
    rotate: 16,
    opacity: 0.22,
    color: "var(--berry-300)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      bottom: 34,
      left: -24
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-why"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "bh-wap-why-copy"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      margin: '0 0 16px'
    }
  }, 'Za to smo napravili Boni asistenticu ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "na WhatsAppu.")), /*#__PURE__*/React.createElement("p", {
    className: "bh-wap-body"
  }, "Boni asistentica je AI koja s tobom priča o tvom psu i pamti sve što joj ispričaš: kakav je, što voli, s kim najradije šeta. Od toga piše njegov Journal, a ti ne moraš ništa ispunjavati.")), /*#__PURE__*/React.createElement(Reveal, {
    delay: 90,
    className: "bh-wap-cardwrap"
  }, /*#__PURE__*/React.createElement(WaProfileCard, {
    desc: "🐶 Dnevnik o tvom psu · 📖 Journal · 💙 Boni Family"
  })))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--white)",
    variant: 1
  }));
}

// 3 · HOW IT WORKS — the section a visitor is actually asking for: how does it
// manage anything. THIS SECTION TELLS AND SHOWS NOTHING. No bubbles here; the
// real messages all live in the showcase band below, and mixing the two is what
// made the old page read as the same thing twice. The heading's second sentence
// deliberately says she WATCHES the deliveries rather than "everything goes
// through the chat" — the latter framed her as a settings screen.
function WaHow() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 84,
    rotate: -14,
    opacity: 0.09,
    color: "var(--teal-600)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 24,
      right: -20
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      textAlign: 'center',
      maxWidth: 820,
      margin: '0 auto var(--space-7)'
    }
  }, 'Boni ti se javi kad te pustimo unutra. ', /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--brand)'
    }
  }, "Od tada piše priču tvog psa."))), /*#__PURE__*/React.createElement(Reveal, {
    delay: 80
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-steps"
  }, WA_STEPS.map((st, i) => /*#__PURE__*/React.createElement("div", {
    key: st.t,
    className: "bh-step-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-step-icon",
    style: {
      background: st.bg
    }
  }, /*#__PURE__*/React.createElement(DoodleIcon, {
    name: st.icon,
    size: 34,
    color: st.color
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-korak"
  }, 'KORAK ', i + 1), /*#__PURE__*/React.createElement("h3", {
    className: "bh-wap-step-t"
  }, st.t), /*#__PURE__*/React.createElement("p", {
    className: "bh-wap-step-s"
  }, st.s))))))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-50)",
    variant: 2
  }));
}

// 4 · THE SHOWCASES — three real conversations, one job each: rescheduling,
// adjusting, leaving. No heading over the band on purpose: three quoted
// situations plus a band title would be one label too many, and the repeated
// layout is the structure. Rows alternate so the eye keeps moving.
function WaShowcases() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--ink-50)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, WA_SHOWCASES.map((c, i) => /*#__PURE__*/React.createElement(Reveal, {
    key: c.q,
    delay: i * 60,
    className: 'bh-wap-show' + (i % 2 === 1 ? ' bh-wap-show--flip' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wap-show-copy"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "bh-wap-showq"
  }, c.q), /*#__PURE__*/React.createElement("p", {
    className: "bh-wap-show-s"
  }, c.s)), /*#__PURE__*/React.createElement(WaThread, {
    msgs: c.thread
  })))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--white)",
    variant: 0
  }));
}

// 5 · THE ASK — the close repeats the chat CTA (the conversion event) and keeps
// the club door open for everyone who would rather not write to anyone. The
// button names the CHANNEL here because the heading right above already names
// her; in the hero it is the other way round.
function WaClose() {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--white)',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("div", {
    className: "bh-join-panel"
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 190,
    rotate: -14,
    opacity: 0.07,
    color: "var(--cream-100)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      right: -44,
      bottom: -52
    }
  }), /*#__PURE__*/React.createElement(Sparkle, {
    size: 18,
    color: "var(--teal-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 34,
      left: '9%'
    }
  }), /*#__PURE__*/React.createElement("h2", {
    className: "bh-h2",
    style: {
      color: 'var(--white)',
      fontSize: 'clamp(1.7rem, 3.4vw, 2.6rem)',
      lineHeight: 1.12,
      maxWidth: 640,
      margin: '0 auto 12px'
    }
  }, "Upoznaj Boni asistenticu."), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: 'var(--font-text)',
      fontSize: 17,
      fontWeight: 600,
      lineHeight: 1.6,
      color: 'var(--cream-200)',
      maxWidth: 500,
      margin: '0 auto 26px'
    }
  }, "Rani pristup je u tijeku. Prijaviš se, a kad te pustimo unutra, Boni ti se javi na WhatsApp."), /*#__PURE__*/React.createElement(WaCta, {
    label: "Prijavi se",
    onDark: true,
    apply: true
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 18,
      fontFamily: 'var(--font-text)',
      fontWeight: 700,
      fontSize: 14.5,
      color: 'var(--cream-300)'
    }
  }, 'Već imaš pristup? ', /*#__PURE__*/React.createElement("a", {
    href: WA_LINK,
    style: {
      color: 'var(--white)',
      textDecoration: 'underline',
      textUnderlineOffset: 3
    }
  }, "Piši Boni na WhatsApp →"))))), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--ink-900)",
    variant: 1
  }));
}
function WhatsappPage() {
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(WaHero, null), /*#__PURE__*/React.createElement(WaWhy, null), /*#__PURE__*/React.createElement(WaHow, null), /*#__PURE__*/React.createElement(WaShowcases, null), /*#__PURE__*/React.createElement(WaClose, null));
}
window.WhatsappPage = WhatsappPage;
/* ══════════════════════════════════════════════════════════════════
   JOURNAL PAGE — boni.hr/pages/journal (2026-09-25, "Plan A")

   The member's private album, on our own site and in our own design, replacing the
   Armory Feed at {{personalUrl}}?f=journal as the place Boni links to.

   HOW IT GETS ITS DATA: the site cannot read the Mindsmiths database, so
   journal_service:1.0 PUSHES a per-member snapshot to Shopify Files as
   jr-<token>.json, rewritten whole on every save and delete. This page reads it from
   /cdn/shop/files/. The CDN caches a bare file URL for a year but serves any NEW ?v=
   from origin, so every load adds ?v=<now> (verified 2026-09-25).

   HOW "LOGIN" WORKS: there is none. Boni sends boni.hr/pages/journal#t=<token> on
   WhatsApp; the token is the key. It sits in the URL FRAGMENT on purpose, so it never
   reaches a server log, an analytics hit or a Referer header. The page keeps it in
   localStorage so the member is never asked again on that device, and strips it from
   the address bar so a screenshot of the page does not leak it. A new device = ask Boni
   for the link again, which the fallback screen does in one tap (prefilled WhatsApp).

   COPY RULES THAT BIND HERE: the dog's name only ever sits in a NOMINATIVE slot (it
   stands alone as a chip; never "Rexov Journal"), genderless, no em dashes.
   ══════════════════════════════════════════════════════════════════ */

const JR_STORE_KEY = 'boniJournalToken';
const JR_WA = 'https://wa.me/385976128956?text=' + encodeURIComponent('Pošalji mi link na moj Journal');
const JR_MONTHS = ['Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];
function jrReadToken() {
  const valid = t => /^[0-9a-f]{32}$/.test(t || '');
  let fromHash = '';
  try {
    const m = (window.location.hash || '').match(/[#&]t=([0-9a-f]{32})/);
    if (m) fromHash = m[1];
  } catch (e) {}
  if (valid(fromHash)) {
    try {
      localStorage.setItem(JR_STORE_KEY, fromHash);
    } catch (e) {}
    try {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (e) {}
    return fromHash;
  }
  try {
    const saved = localStorage.getItem(JR_STORE_KEY);
    if (valid(saved)) return saved;
  } catch (e) {}
  return '';
}

// Croatian plural: 1 / 2-4 / 5+ (11-14 always take the 5+ form)
function jrPl(n, one, few, many) {
  const d = n % 10,
    h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

// 1 trenutak / 2-4 trenutka / 5+ trenutaka (11-14 always trenutaka)
function jrMoments(n) {
  const d = n % 10,
    h = n % 100;
  if (d === 1 && h !== 11) return n + ' trenutak';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return n + ' trenutka';
  return n + ' trenutaka';
}
function jrGroupByMonth(entries) {
  const groups = [];
  entries.forEach(e => {
    const d = e.at ? new Date(e.at) : null;
    const key = d && !isNaN(d) ? JR_MONTHS[d.getMonth()] + ' ' + d.getFullYear() : 'Ranije';
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = {
        key,
        items: []
      };
      groups.push(g);
    }
    g.items.push(e);
  });
  return groups;
}
function JrButton({
  label,
  href,
  onDark = false
}) {
  const {
    Button
  } = window.BoniDesignSystem_dec8ad;
  return /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "lg",
    iconRight: /*#__PURE__*/React.createElement("span", null, "→"),
    style: onDark ? {
      background: 'var(--white)',
      color: 'var(--ink-900)',
      border: 'none'
    } : {
      background: 'var(--brand)',
      color: 'var(--white)',
      border: 'none'
    },
    onClick: () => {
      window.location.href = href;
    }
  }, label);
}

// One saved moment: a photo "print" with the date on a chip and the caption under it.
// Alternating tilt is on an INNER div (the S107 rule: .bh-reveal.bh-in sets transform:none).
function JrCard({
  e,
  i,
  onOpen
}) {
  const tilt = [-1.2, 0.9, -0.5, 1.3][i % 4];
  return /*#__PURE__*/React.createElement(Reveal, {
    delay: Math.min(i, 6) * 50,
    className: "bh-jr-cell"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "bh-jr-card",
    style: {
      transform: 'rotate(' + tilt + 'deg)'
    },
    onClick: () => onOpen(e)
  }, /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-photo"
  }, e.image ? /*#__PURE__*/React.createElement("img", {
    src: e.image,
    alt: e.caption || 'Trenutak iz Journala',
    loading: "lazy"
  }) : null), /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-date"
  }, e.date), e.caption ? /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-cap"
  }, e.caption) : null));
}
function JrLightbox({
  e,
  onClose
}) {
  React.useEffect(() => {
    const k = ev => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', k);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', k);
      document.body.style.overflow = '';
    };
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: "bh-jr-lb",
    role: "dialog",
    "aria-modal": "true",
    onClick: onClose
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "bh-jr-lb-x",
    "aria-label": "Zatvori",
    onClick: onClose
  }, "×"), /*#__PURE__*/React.createElement("figure", {
    className: "bh-jr-lb-fig",
    onClick: ev => ev.stopPropagation()
  }, /*#__PURE__*/React.createElement("img", {
    src: e.image,
    alt: e.caption || 'Trenutak iz Journala'
  }), /*#__PURE__*/React.createElement("figcaption", null, /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-date"
  }, e.date), e.caption ? /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-lb-cap"
  }, e.caption) : null)));
}

// ── THE DIARY (snapshot v3, round 3, 2026-09-25) ───────────────────────────────
// User feedback on the pinboard: too modern and soulless for a personal journal, the
// Instagram-style story player and square album grid do not fit us, every pin put its date
// and title somewhere else, and the board overall read as messy. So the same composed list
// (journal_service is still the editor, the page decides nothing about content) is now laid
// out as a DIARY:
//   - ONE column, grouped by DAY. The date is written by hand in the margin (desktop) or
//     above the day (phone), so it is always in the same place and never inside a card.
//   - ONE card anatomy for every kind: [media] -> label row -> title -> text. Kinds only
//     change the paper, never where things sit.
//   - Two voices, kept apart on purpose: HANDWRITING = time and Boni (dates, her notes, her
//     signature); PRINT = the dog's story (titles and text).
//   - ONE viewer for stories, albums and single moments: a stack of cards you swipe through
//     at your own pace (no progress bars, no auto-advance).
// Copy rules still bind: the dog's name only in a nominative slot, genderless, no em dashes.

const JD_SEEN_KEY = 'boniJournalSeen';
const JD_WEEK = ['nedjelja', 'ponedjeljak', 'utorak', 'srijeda', 'četvrtak', 'petak', 'subota'];
const JD_MON = ['siječnja', 'veljače', 'ožujka', 'travnja', 'svibnja', 'lipnja', 'srpnja', 'kolovoza', 'rujna', 'listopada', 'studenoga', 'prosinca'];
function jdDay(at) {
  const d = at ? new Date(at) : null;
  if (!d || isNaN(d)) return {
    key: 'x',
    day: '',
    mon: '',
    wd: ''
  };
  return {
    key: d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate(),
    day: d.getDate() + '.',
    mon: JD_MON[d.getMonth()],
    wd: JD_WEEK[d.getDay()]
  };
}

// [{head, days:[{day, items}]}]: split at month/story items, then group by calendar day
function jdSections(items) {
  const secs = [];
  let cur = {
    head: null,
    days: []
  };
  items.forEach(it => {
    if (it.type === 'month' || it.type === 'story') {
      if (cur.head || cur.days.length) secs.push(cur);
      cur = {
        head: it,
        days: []
      };
      return;
    }
    const d = jdDay(it.at);
    let g = cur.days[cur.days.length - 1];
    if (!g || g.day.key !== d.key) {
      g = {
        day: d,
        items: []
      };
      cur.days.push(g);
    }
    g.items.push(it);
  });
  if (cur.head || cur.days.length) secs.push(cur);
  return secs;
}

// what the label row says: one place for "what kind of moment is this"
function jdLabel(it) {
  if (it.first) return {
    text: 'Prvi put · ' + it.first,
    tone: 'first'
  };
  if (it.type === 'quote') return {
    text: 'Tvojim riječima',
    tone: 'quote'
  };
  if (it.type === 'observation') return {
    text: 'Boni je primijetila',
    tone: 'boni'
  };
  if (it.type === 'surprise') return {
    text: '✨ ' + (it.label || 'Boni je napravila'),
    tone: 'boni'
  };
  if (it.type === 'album') return {
    text: 'Novi album',
    tone: 'album'
  };
  if (it.type === 'welcome') return {
    text: 'Prva stranica',
    tone: 'plain'
  };
  if (it.type === 'cluster') return {
    text: it.images.length + ' fotke',
    tone: 'plain'
  };
  return null;
}
function JdBody({
  it,
  isNew
}) {
  const lbl = jdLabel(it);
  const hand = it.type === 'observation';
  return /*#__PURE__*/React.createElement("div", {
    className: "jd-body"
  }, lbl || isNew ? /*#__PURE__*/React.createElement("div", {
    className: "jd-lbl"
  }, lbl ? /*#__PURE__*/React.createElement("span", {
    className: 'jd-tag jd-tag--' + lbl.tone
  }, lbl.text) : null, isNew ? /*#__PURE__*/React.createElement("span", {
    className: "jd-new"
  }, "novo") : null) : null, /*#__PURE__*/React.createElement("h3", {
    className: 'jd-t' + (hand ? ' jd-t--hand' : '') + (it.type === 'quote' ? ' jd-t--quote' : '')
  }, hand ? it.text : it.title), !hand && it.text ? /*#__PURE__*/React.createElement("p", {
    className: "jd-x"
  }, it.text) : null, hand ? /*#__PURE__*/React.createElement("span", {
    className: "jd-sign"
  }, "Boni") : null, it.lines ? /*#__PURE__*/React.createElement("dl", {
    className: "jd-lines"
  }, it.lines.map(l => /*#__PURE__*/React.createElement("div", {
    key: l[0]
  }, /*#__PURE__*/React.createElement("dt", null, l[0]), /*#__PURE__*/React.createElement("dd", null, l[1])))) : null, it.ai ? /*#__PURE__*/React.createElement("p", {
    className: "jd-ai"
  }, "Izradila AI, od tvoje fotografije.") : null);
}
function JdMedia({
  it
}) {
  if (it.type === 'album') {
    const a = it.album || {};
    const pics = (a.items || []).filter(x => x.image).slice(0, 3).map(x => x.image);
    if (!pics.length && it.image) pics.push(it.image);
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-media jd-fan",
      "aria-hidden": "true"
    }, pics.map((src, k) => /*#__PURE__*/React.createElement("img", {
      key: k,
      className: 'jd-fan-' + k,
      src: src,
      alt: "",
      loading: "lazy"
    })));
  }
  if (it.type === 'cluster') {
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-media"
    }, /*#__PURE__*/React.createElement("div", {
      className: "jd-print"
    }, /*#__PURE__*/React.createElement("img", {
      src: it.images[0],
      alt: it.title,
      loading: "lazy"
    })), /*#__PURE__*/React.createElement("div", {
      className: "jd-thumbs"
    }, it.images.slice(1, 4).map((src, k) => /*#__PURE__*/React.createElement("img", {
      key: k,
      src: src,
      alt: "",
      loading: "lazy"
    }))));
  }
  if (!it.image || it.type === 'welcome') return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "jd-media"
  }, /*#__PURE__*/React.createElement("div", {
    className: "jd-print"
  }, /*#__PURE__*/React.createElement("img", {
    src: it.image,
    alt: it.title,
    loading: "lazy"
  })));
}
function JdCard({
  it,
  isNew,
  onOpen
}) {
  const media = /*#__PURE__*/React.createElement(JdMedia, {
    it: it
  });
  const hasMedia = !!(it.image && it.type !== 'welcome') || it.type === 'cluster' || it.type === 'album';
  const big = it.size === 'L' && hasMedia && it.type !== 'album';
  const cls = 'jd-card jd-card--' + it.type + (hasMedia ? ' jd-card--media' : '') + (big ? ' jd-card--big' : '');
  const clickable = it.type !== 'observation' && !(it.type === 'surprise' && !it.image);
  const inner = /*#__PURE__*/React.createElement(React.Fragment, null, hasMedia ? media : null, /*#__PURE__*/React.createElement(JdBody, {
    it: it,
    isNew: isNew
  }), it.type === 'album' ? /*#__PURE__*/React.createElement("span", {
    className: "jd-more"
  }, "Prolistaj ›") : null);
  if (!clickable) return /*#__PURE__*/React.createElement("div", {
    className: cls
  }, inner);
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: cls,
    onClick: () => onOpen(it)
  }, inner);
}
function JdDate({
  day
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "jd-date",
    "aria-label": day.wd + ', ' + day.day + ' ' + day.mon
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-date-d"
  }, day.day), /*#__PURE__*/React.createElement("span", {
    className: "jd-date-m"
  }, day.mon), /*#__PURE__*/React.createElement("span", {
    className: "jd-date-w"
  }, day.wd));
}
function JdChapter({
  head,
  onStory,
  pinnedAbove,
  live
}) {
  const month = head.label || '';
  if (head.type === 'story' && !pinnedAbove) {
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "jd-chapter jd-chapter--story",
      onClick: () => onStory(head.story)
    }, head.image ? /*#__PURE__*/React.createElement("span", {
      className: "jd-chapter-pic"
    }, /*#__PURE__*/React.createElement("img", {
      src: head.image,
      alt: "",
      loading: "lazy"
    })) : null, /*#__PURE__*/React.createElement("span", {
      className: "jd-chapter-txt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "jd-chapter-m"
    }, month), /*#__PURE__*/React.createElement("span", {
      className: "jd-chapter-t"
    }, head.title), head.firsts && head.firsts.length ? /*#__PURE__*/React.createElement("span", {
      className: "jd-chips"
    }, head.firsts.map(f => /*#__PURE__*/React.createElement("span", {
      key: f,
      className: "jd-chip"
    }, f))) : null, /*#__PURE__*/React.createElement("span", {
      className: "jd-btn"
    }, "Prolistaj priču ›")));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "jd-chapter jd-chapter--plain"
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-chapter-m jd-chapter-m--big"
  }, month), live ? /*#__PURE__*/React.createElement("span", {
    className: "jd-chapter-sub"
  }, live) : null, pinnedAbove ? /*#__PURE__*/React.createElement("span", {
    className: "jd-chapter-sub"
  }, "priča mjeseca je gore ↑") : null);
}

// ── the viewer: one stack of cards for stories, albums and moments ──
function JdDeckCard({
  c
}) {
  const [src, setSrc] = React.useState(false);
  if (c.kind === 'cover') {
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-dc jd-dc--cover"
    }, c.image ? /*#__PURE__*/React.createElement("div", {
      className: "jd-dc-pic"
    }, /*#__PURE__*/React.createElement("img", {
      src: c.image,
      alt: ""
    })) : null, /*#__PURE__*/React.createElement("span", {
      className: "jd-dc-date"
    }, c.month), /*#__PURE__*/React.createElement("h2", {
      className: "jd-dc-t jd-dc-t--big"
    }, c.title), c.firsts && c.firsts.length ? /*#__PURE__*/React.createElement("span", {
      className: "jd-chips"
    }, c.firsts.map(f => /*#__PURE__*/React.createElement("span", {
      key: f,
      className: "jd-chip"
    }, f))) : null, /*#__PURE__*/React.createElement("span", {
      className: "jd-dc-hint"
    }, "Povuci karticu ili koristi strelice"));
  }
  if (c.kind === 'miles') {
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-dc jd-dc--paper"
    }, /*#__PURE__*/React.createElement("span", {
      className: "jd-tag jd-tag--first"
    }, "Prvi put"), /*#__PURE__*/React.createElement("ul", {
      className: "jd-dc-miles"
    }, c.items.map(m => /*#__PURE__*/React.createElement("li", {
      key: m.label
    }, /*#__PURE__*/React.createElement("span", {
      className: "jd-dc-mdate"
    }, m.date), m.label))));
  }
  if (c.kind === 'stats') {
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-dc jd-dc--boni"
    }, /*#__PURE__*/React.createElement("span", {
      className: "jd-tag jd-tag--boni"
    }, "Boni je primijetila"), c.lines.map(l => /*#__PURE__*/React.createElement("p", {
      key: l,
      className: "jd-dc-hand"
    }, l)), /*#__PURE__*/React.createElement("span", {
      className: "jd-sign"
    }, "Boni"));
  }
  if (c.kind === 'idea') {
    return /*#__PURE__*/React.createElement("div", {
      className: "jd-dc jd-dc--idea"
    }, /*#__PURE__*/React.createElement("span", {
      className: "jd-tag jd-tag--plain"
    }, "Ideja za idući mjesec"), /*#__PURE__*/React.createElement("p", {
      className: "jd-dc-hand"
    }, c.text), /*#__PURE__*/React.createElement("span", {
      className: "jd-sign"
    }, "Boni"));
  }
  const it = c.it;
  const d = jdDay(it.at);
  return /*#__PURE__*/React.createElement("div", {
    className: 'jd-dc' + (it.image ? '' : ' jd-dc--paper')
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-dc-date"
  }, it.at ? d.wd + ', ' + d.day + ' ' + d.mon : it.date), it.image ? /*#__PURE__*/React.createElement("div", {
    className: "jd-dc-pic"
  }, /*#__PURE__*/React.createElement("img", {
    src: it.image,
    alt: it.title
  })) : null, /*#__PURE__*/React.createElement(JdBody, {
    it: it,
    isNew: false
  }), it.source && it.source.length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-src"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "jd-src-btn",
    onPointerDown: e => e.stopPropagation(),
    onClick: () => setSrc(!src)
  }, src ? 'Sakrij' : 'Kako je nastalo?'), src ? /*#__PURE__*/React.createElement("div", {
    className: "jd-src-thread"
  }, it.source.map((m, i) => /*#__PURE__*/React.createElement("span", {
    key: i
  }, m.text))) : null) : null);
}
function JdDeck({
  title,
  cards,
  start,
  onClose
}) {
  const [idx, setIdx] = React.useState(Math.min(start || 0, cards.length - 1));
  const [dx, setDx] = React.useState(0);
  const [fly, setFly] = React.useState(0); // -1 / 1 while a card leaves
  const drag = React.useRef(null);
  const last = idx >= cards.length - 1;
  const go = d => {
    const n = idx + d;
    if (n < 0 || n >= cards.length || fly) {
      setDx(0);
      return;
    }
    setFly(d > 0 ? -1 : 1);
    setTimeout(() => {
      setIdx(n);
      setFly(0);
      setDx(0);
    }, 200);
  };
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    const k = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('keydown', k);
      document.body.style.overflow = '';
    };
  });
  const onDown = e => {
    drag.current = {
      x: e.clientX,
      id: e.pointerId
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {}
  };
  const onMove = e => {
    if (drag.current) setDx(e.clientX - drag.current.x);
  };
  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    if (dx < -70) go(1);else if (dx > 70) go(-1);else setDx(0);
  };
  const tx = fly ? fly * 130 + 'vw' : dx + 'px';
  const rot = fly ? fly * 14 : dx / 22;
  return /*#__PURE__*/React.createElement("div", {
    className: "jd-deck",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": title
  }, /*#__PURE__*/React.createElement("div", {
    className: "jd-deck-top"
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-deck-title"
  }, title), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "jd-deck-x",
    "aria-label": "Zatvori",
    onClick: onClose
  }, "×")), /*#__PURE__*/React.createElement("div", {
    className: "jd-deck-stack"
  }, idx + 2 < cards.length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-dc-ghost jd-dc-ghost--2",
    "aria-hidden": "true"
  }) : null, idx + 1 < cards.length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-dc-ghost jd-dc-ghost--1",
    "aria-hidden": "true"
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: 'jd-dc-wrap' + (drag.current ? ' is-drag' : ''),
    key: idx,
    style: {
      transform: 'translateX(' + tx + ') rotate(' + rot + 'deg)'
    },
    onPointerDown: onDown,
    onPointerMove: onMove,
    onPointerUp: onUp,
    onPointerCancel: onUp
  }, /*#__PURE__*/React.createElement(JdDeckCard, {
    c: cards[idx]
  }))), /*#__PURE__*/React.createElement("div", {
    className: "jd-deck-nav"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "jd-deck-arrow",
    "aria-label": "Prethodna",
    disabled: idx === 0,
    onClick: () => go(-1)
  }, "‹"), /*#__PURE__*/React.createElement("span", {
    className: "jd-deck-count"
  }, idx + 1, " / ", cards.length), last ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "jd-deck-arrow jd-deck-arrow--done",
    onClick: onClose
  }, "Gotovo") : /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "jd-deck-arrow",
    "aria-label": "Sljedeća",
    onClick: () => go(1)
  }, "›")));
}
function JrBoard({
  data,
  demo,
  forget,
  token
}) {
  const [deck, setDeck] = React.useState(null); // {title, cards, start}
  const [seen] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem(JD_SEEN_KEY) || '{}')[token] || '';
    } catch (e) {
      return '';
    }
  });
  React.useEffect(() => {
    // remember this visit, so next time only newer moments carry "novo"
    try {
      const m = JSON.parse(localStorage.getItem(JD_SEEN_KEY) || '{}');
      m[token] = data.updatedAt || new Date().toISOString();
      localStorage.setItem(JD_SEEN_KEY, JSON.stringify(m));
    } catch (e) {}
  }, []);
  const items = data.items || [];
  const albums = data.albums || {};
  const tops = data.top || [];
  const pinnedStory = (tops.find(t => t.type === 'story') || {}).story;
  const moments = items.filter(i => ['photo', 'cluster', 'note', 'quote', 'surprise', 'howmet'].includes(i.type) && (i.image || i.images || i.title));
  const chrono = moments.slice().reverse();
  const flat = x => x.type === 'cluster' ? x.images.map((src, k) => ({
    ...x,
    image: src,
    id: x.id + '-' + k,
    text: k ? '' : x.text
  })) : [x];
  const openMoment = it => {
    if (it.type === 'album') {
      const a = albums[it.opens];
      if (a) setDeck({
        title: 'Album · ' + a.title,
        cards: a.items.slice().reverse().map(x => ({
          kind: 'moment',
          it: x
        })),
        start: 0
      });
      return;
    }
    const list = [].concat(...chrono.map(flat));
    const start = Math.max(0, list.findIndex(x => x.id === it.id || x.id === it.id + '-0'));
    setDeck({
      title: data.dog ? data.dog + ' · Journal' : 'Journal',
      cards: list.map(x => ({
        kind: 'moment',
        it: x
      })),
      start
    });
  };
  const openStory = sid => {
    const st = (data.stories || {})[sid];
    if (!st) return;
    const head = items.find(h => h.story === sid) || {};
    const cards = st.slides.map(s => s.kind === 'photo' || s.kind === 'note' ? {
      kind: 'moment',
      it: s.it
    } : s.kind === 'cover' ? {
      ...s,
      firsts: head.firsts
    } : s);
    setDeck({
      title: st.month + ' · ' + st.title,
      cards,
      start: 0
    });
  };
  const isNew = it => !!(seen && it.at && it.at > seen && !demo);

  // the profile photo stays put: the first photo ever (the welcome one), never the newest
  const welcome = items.find(i => i.type === 'welcome') || {};
  const avatar = welcome.image || (chrono.find(m => m.image && m.type !== 'surprise') || {}).image;
  const p = data.profile || {};
  const facts = [p.breed, p.age].filter(Boolean);
  const now = new Date();
  const curKey = now.getFullYear() + '-' + now.getMonth();
  const sections = jdSections(items).map(s => ({
    ...s,
    days: s.days.map(g => ({
      ...g,
      items: g.items.map(it => it.type === 'album' ? {
        ...it,
        album: albums[it.opens]
      } : it)
    }))
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: "jd"
  }, /*#__PURE__*/React.createElement("header", {
    className: "jd-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "jd-av"
  }, avatar ? /*#__PURE__*/React.createElement("img", {
    src: avatar,
    alt: ""
  }) : /*#__PURE__*/React.createElement("span", null, "🐾")), /*#__PURE__*/React.createElement("div", {
    className: "jd-head-txt"
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-head-k"
  }, "Journal"), /*#__PURE__*/React.createElement("h1", {
    className: "jd-name"
  }, data.dog || 'Journal'), facts.length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-facts"
  }, facts.join(' · ')) : null, data.line ? /*#__PURE__*/React.createElement("p", {
    className: "jd-line"
  }, data.line) : null, /*#__PURE__*/React.createElement("div", {
    className: "jd-counts"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, data.count), " ", jrPl(data.count, 'trenutak', 'trenutka', 'trenutaka')), data.firsts ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, data.firsts), " ", jrPl(data.firsts, 'prvi put', 'prva puta', 'prvih puta')) : null, data.since ? /*#__PURE__*/React.createElement("span", null, data.since) : null))), demo ? /*#__PURE__*/React.createElement("p", {
    className: "jd-demo"
  }, "Demo: izmišljeni pas i razgovor. Dodirni bilo što.") : null, !demo && data.banner ? /*#__PURE__*/React.createElement("p", {
    className: "jd-demo"
  }, data.banner) : null, tops.length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-tops"
  }, tops.map(t => t.type === 'story' ? /*#__PURE__*/React.createElement(JdChapter, {
    key: t.id,
    head: t,
    onStory: openStory
  }) : /*#__PURE__*/React.createElement("div", {
    key: t.id,
    className: 'jd-sticky jd-sticky--' + t.type
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-sticky-k"
  }, t.type === 'countdown' ? 'Uskoro' : t.title), /*#__PURE__*/React.createElement("p", {
    className: "jd-sticky-t"
  }, t.type === 'countdown' ? t.title + ' ' + t.text : t.text), t.type === 'idea' ? /*#__PURE__*/React.createElement("span", {
    className: "jd-sign"
  }, "Boni") : null))) : null, sections.map((s, si) => {
    let live = '';
    if (s.head && s.head.type === 'month') {
      const hd = new Date(s.head.at);
      if (hd.getFullYear() + '-' + hd.getMonth() === curKey || demo) {
        const n = s.days.reduce((a, g) => a + g.items.filter(x => ['photo', 'cluster', 'note', 'quote'].includes(x.type)).length, 0);
        live = n ? 'do sada ' + n + ' ' + jrPl(n, 'trenutak', 'trenutka', 'trenutaka') : '';
      }
    }
    return /*#__PURE__*/React.createElement("section", {
      key: si,
      className: "jd-sec"
    }, s.head ? /*#__PURE__*/React.createElement(JdChapter, {
      head: s.head,
      onStory: openStory,
      pinnedAbove: s.head.story && s.head.story === pinnedStory,
      live: live
    }) : null, s.days.map(g => /*#__PURE__*/React.createElement("div", {
      key: g.day.key + si,
      className: "jd-day"
    }, /*#__PURE__*/React.createElement(JdDate, {
      day: g.day
    }), /*#__PURE__*/React.createElement("div", {
      className: "jd-entries"
    }, g.items.map(it => /*#__PURE__*/React.createElement(JdCard, {
      key: it.id,
      it: it,
      isNew: isNew(it),
      onOpen: openMoment
    }))))));
  }), /*#__PURE__*/React.createElement("section", {
    className: "jd-about"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "jd-about-h"
  }, 'Ovo je ', data.dog || 'tvoj pas'), /*#__PURE__*/React.createElement("dl", {
    className: "jd-facts-card"
  }, p.breed ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("dt", null, "Pasmina"), /*#__PURE__*/React.createElement("dd", null, p.breed)) : null, p.age ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("dt", null, "Dob"), /*#__PURE__*/React.createElement("dd", null, p.age)) : null, p.loves && p.loves.length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("dt", null, "Najviše voli"), /*#__PURE__*/React.createElement("dd", null, p.loves.join(', '))) : null, data.since ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("dt", null, "U Boni Familyju"), /*#__PURE__*/React.createElement("dd", null, data.since.replace('Član od ', 'od ').replace(/\.$/, ''))) : null), (p.prompts || []).length ? /*#__PURE__*/React.createElement("div", {
    className: "jd-prompts"
  }, p.prompts.map(pr => /*#__PURE__*/React.createElement("div", {
    key: pr.q,
    className: "jd-prompt"
  }, /*#__PURE__*/React.createElement("span", {
    className: "jd-prompt-q"
  }, pr.q), /*#__PURE__*/React.createElement("span", {
    className: "jd-prompt-a"
  }, pr.a)))) : null), /*#__PURE__*/React.createElement("p", {
    className: "jd-foot"
  }, 'Ovo je tvoj privatni Journal. Na zajedničkom uređaju? ', /*#__PURE__*/React.createElement("a", {
    onClick: forget,
    className: "bh-jr-forget"
  }, "Zaboravi ovaj uređaj")), deck ? /*#__PURE__*/React.createElement(JdDeck, {
    title: deck.title,
    cards: deck.cards,
    start: deck.start,
    onClose: () => setDeck(null)
  }) : null);
}

// ── v4: THE DIARY (2026-10-17, off the Melkior/Ares review) ─────────────────────────────
// Writing rules every snapshot must follow (they are what makes this work for ANY owner):
//   - "ti" by default ("Otvoriš čips. Publika je već tu."). NAME a person only when WHO it is
//     is the point of the moment: exclusivity ("Večernja šetnja ide samo s Melkiorom"), a
//     contrast between people ("Melkior kaže ne. Andrea popusti."), a first with someone
//     specific. Otherwise names read as spam. Never "vas obožava"-style lines that put Boni outside the family; she says "ja"
//     only in her signed handwritten notes.
//   - TENSE carries meaning: PRESENT for habits and character, including what a story taught
//     him ("Jednom podigneš glas dok radiš i on to zapamti zauvijek."), PAST for a one-off event ("Šef je pregledao bor i odobrio ga.").
//   - CHARACTER over report: never just explain what happened. Find the angle only THIS dog has
//     (a nickname, his inner logic, a dry punchline), grounded in what the owner said or the
//     photo shows. If a line could describe any dog, rewrite it. Funny, plus a few tender ones.
//   - MESSAGE = the moment itself, readable alone (about 12 words max). CONTEXT = one or two
//     short lines: the punchline or the feeling. Context is optional.
//   - Boni speaks ONLY in handwritten notes, signed. Owner's exact words go in a quote, verbatim.
//   - Names are declined properly in Boni-written text ("s Melkiorom"); the nominative-only
//     rule binds TEMPLATED slots (WhatsApp templates, fixed UI copy), not her prose. No em dashes.
// Design rules: no tags, no card boxes. Prints are taped straight onto the page, the message
// is the biggest text, the date is handwritten in the margin. Sort + search + year are the
// only tools; there are no categories.
const JV_MON = ['siječnja', 'veljače', 'ožujka', 'travnja', 'svibnja', 'lipnja', 'srpnja', 'kolovoza', 'rujna', 'listopada', 'studenoga', 'prosinca'];
const jvFold = s => String(s || '').toLowerCase().replace(/[čć]/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z').replace(/đ/g, 'd');
function jvDay(e) {
  const d = new Date(e.at);
  if (e.fuzzy) return {
    key: 'f-' + e.fuzzy,
    d: '',
    m: e.fuzzy,
    y: d.getFullYear()
  };
  return {
    key: e.at.slice(0, 10),
    d: d.getDate() + '.',
    m: JV_MON[d.getMonth()],
    y: d.getFullYear()
  };
}
function JvPrints({
  e,
  onOpen
}) {
  const imgs = e.images || [];
  if (!imgs.length) return null;
  const shown = imgs.slice(0, 3);
  return /*#__PURE__*/React.createElement("div", {
    className: 'jv-prints jv-prints--' + Math.min(imgs.length, 3)
  }, shown.map((src, k) => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: k,
    className: "jv-print",
    onClick: () => onOpen(e, k),
    "aria-label": "Otvori fotografiju"
  }, /*#__PURE__*/React.createElement("img", {
    src: src,
    alt: "",
    loading: "lazy"
  }), k === 2 && imgs.length > 3 ? /*#__PURE__*/React.createElement("span", {
    className: "jv-more"
  }, "+", imgs.length - 3) : null)));
}
function JvEntry({
  e,
  owner,
  onOpen,
  isNew
}) {
  const nw = isNew ? ' jv-fresh' : '';
  const tag = isNew ? /*#__PURE__*/React.createElement("span", {
    className: "jv-novo"
  }, "novo") : null;
  if (e.kind === 'boni') {
    return /*#__PURE__*/React.createElement("div", {
      className: 'jv-boni' + nw
    }, tag, (e.images || []).length ? /*#__PURE__*/React.createElement("div", {
      className: "jv-boni-pics"
    }, e.images.map((src, k) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "jv-print jv-print--sm",
      onClick: () => onOpen(e, k),
      "aria-label": "Otvori fotografiju"
    }, /*#__PURE__*/React.createElement("img", {
      src: src,
      alt: "",
      loading: "lazy"
    })))) : null, /*#__PURE__*/React.createElement("p", {
      className: "jv-boni-t"
    }, e.message), /*#__PURE__*/React.createElement("span", {
      className: "jv-sign"
    }, "Boni"));
  }
  if (e.kind === 'quote') {
    return /*#__PURE__*/React.createElement("figure", {
      className: 'jv-quote' + nw
    }, tag, /*#__PURE__*/React.createElement("blockquote", null, '„' + e.message + '”'), owner ? /*#__PURE__*/React.createElement("figcaption", null, owner) : null);
  }
  const scrap = !(e.images || []).length;
  return /*#__PURE__*/React.createElement("div", {
    className: 'jv-entry' + (scrap ? ' jv-entry--scrap' : '') + nw
  }, tag, /*#__PURE__*/React.createElement(JvPrints, {
    e: e,
    onOpen: onOpen
  }), /*#__PURE__*/React.createElement("h3", {
    className: "jv-msg"
  }, e.message), e.context ? /*#__PURE__*/React.createElement("p", {
    className: "jv-ctx"
  }, e.context) : null);
}

// The round profile photo. The editor sends the dog's head as a box ({x, y, w, h}, 0-1 of the
// image); once the real image size is known we crop a square around the head so it is always in
// frame. No box (older pages) = plain centred cover crop.
function JvAvatar({
  src,
  box
}) {
  const [dim, setDim] = React.useState(null);
  let style = null;
  if (box && dim && box.w > 0 && box.h > 0) {
    const W = dim[0],
      H = dim[1];
    const cx = (box.x + box.w / 2) * W,
      cy = (box.y + box.h / 2) * H;
    // the head (ears to chin) fills ~90% of the circle; never larger than the image allows
    const side = Math.min(Math.max(box.w * W, box.h * H) / 0.9, W, H);
    const left = Math.min(Math.max(cx - side / 2, 0), W - side);
    const top = Math.min(Math.max(cy - side / 2, 0), H - side);
    style = {
      position: 'absolute',
      maxWidth: 'none',
      objectFit: 'fill',
      width: W / side * 100 + '%',
      height: H / side * 100 + '%',
      left: -left / side * 100 + '%',
      top: -top / side * 100 + '%'
    };
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "jv-av",
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: src,
    alt: "",
    style: style || undefined,
    onLoad: ev => setDim([ev.target.naturalWidth, ev.target.naturalHeight])
  }));
}
function JvDiary({
  data,
  fresh
}) {
  const all = data.entries || [];
  const [order, setOrder] = React.useState('new');
  const [q, setQ] = React.useState('');
  const [year, setYear] = React.useState('');
  const [deck, setDeck] = React.useState(null);
  const years = Array.from(new Set(all.map(e => String(new Date(e.at).getFullYear())))).sort().reverse();
  const fq = jvFold(q.trim());
  const list = all.filter(e => !year || String(new Date(e.at).getFullYear()) === year).filter(e => {
    if (!fq) return true;
    const d = jvDay(e);
    return jvFold([e.message, e.context, d.m, d.y].join(' ')).includes(fq);
  }).sort((a, b) => order === 'new' ? a.at < b.at ? 1 : -1 : a.at < b.at ? -1 : 1);

  // years -> days -> entries, in the chosen order
  const blocks = [];
  list.forEach(e => {
    const d = jvDay(e);
    let y = blocks[blocks.length - 1];
    if (!y || y.y !== d.y) {
      y = {
        y: d.y,
        days: []
      };
      blocks.push(y);
    }
    let g = y.days[y.days.length - 1];
    if (!g || g.key !== d.key) {
      g = {
        key: d.key,
        d,
        items: []
      };
      y.days.push(g);
    }
    g.items.push(e);
  });
  const open = (e, k) => {
    const pics = [];
    list.forEach(x => (x.images || []).forEach((src, i) => pics.push({
      x,
      src,
      i
    })));
    const start = Math.max(0, pics.findIndex(p => p.x === e && p.i === k));
    const cards = pics.map(p => ({
      kind: 'moment',
      it: {
        id: p.x.id + '-' + p.i,
        type: 'photo',
        at: p.x.at,
        image: p.src,
        title: p.i === 0 ? p.x.message : '',
        text: p.i === 0 ? p.x.context || '' : ''
      }
    }));
    setDeck({
      cards,
      start
    });
  };
  const n = list.length;
  return /*#__PURE__*/React.createElement("div", {
    className: "jd jv"
  }, /*#__PURE__*/React.createElement("header", {
    className: "jv-head"
  }, data.photo ? /*#__PURE__*/React.createElement(JvAvatar, {
    src: data.photo,
    box: data.photoBox
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: "jv-head-txt"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "jv-name"
  }, data.dog), data.line ? /*#__PURE__*/React.createElement("p", {
    className: "jv-line"
  }, data.line) : null, (data.facts || []).length ? /*#__PURE__*/React.createElement("p", {
    className: "jv-facts"
  }, data.facts.join(' · ')) : null)), /*#__PURE__*/React.createElement("div", {
    className: "jv-tools"
  }, /*#__PURE__*/React.createElement("div", {
    className: "jv-seg",
    role: "group",
    "aria-label": "Redoslijed"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-pressed": order === 'new',
    onClick: () => setOrder('new')
  }, "Najnovije"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-pressed": order === 'old',
    onClick: () => setOrder('old')
  }, "Najstarije")), /*#__PURE__*/React.createElement("label", {
    className: "jv-search"
  }, /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    r: "7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m20 20-3.5-3.5"
  })), /*#__PURE__*/React.createElement("input", {
    type: "search",
    value: q,
    onChange: ev => setQ(ev.target.value),
    placeholder: "Traži, npr. more ili čips",
    "aria-label": "Traži"
  })), /*#__PURE__*/React.createElement("div", {
    className: "jv-years"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-pressed": !year,
    onClick: () => setYear('')
  }, "Sve"), years.map(y => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: y,
    "aria-pressed": year === y,
    onClick: () => setYear(y)
  }, y)))), !n ? /*#__PURE__*/React.createElement("p", {
    className: "jv-empty"
  }, "Ništa za „", q, "”. Probaj drugu riječ.") : null, blocks.map(b => /*#__PURE__*/React.createElement("section", {
    key: b.y,
    className: "jv-year"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "jv-yh"
  }, b.y), b.days.map(g => /*#__PURE__*/React.createElement("div", {
    key: g.key,
    className: "jv-day"
  }, /*#__PURE__*/React.createElement("div", {
    className: "jv-date"
  }, g.d.d ? /*#__PURE__*/React.createElement("span", {
    className: "jv-date-d"
  }, g.d.d) : null, /*#__PURE__*/React.createElement("span", {
    className: "jv-date-m"
  }, g.d.m)), /*#__PURE__*/React.createElement("div", {
    className: "jv-items"
  }, g.items.map(e => /*#__PURE__*/React.createElement(JvEntry, {
    key: e.id,
    e: e,
    owner: data.name,
    onOpen: open,
    isNew: !!(fresh && fresh[e.id])
  }))))))), deck ? /*#__PURE__*/React.createElement(JdDeck, {
    title: data.dog + ' · Journal',
    cards: deck.cards,
    start: deck.start,
    onClose: () => setDeck(null)
  }) : null);
}

// Shared frame for the three non-album states (loading, no link, empty, error).
function JrNotice({
  title,
  body,
  cta,
  ctaHref
}) {
  return /*#__PURE__*/React.createElement("section", {
    className: "bh-section",
    style: {
      background: 'var(--white)',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(PawMark, {
    size: 120,
    rotate: -14,
    opacity: 0.08,
    color: "var(--ink-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 30,
      right: '6%'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-jr-notice"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "bh-h2",
    style: {
      margin: '0 0 14px'
    }
  }, title), body ? /*#__PURE__*/React.createElement("p", {
    className: "bh-jr-sub"
  }, body) : null, cta ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 26
    }
  }, /*#__PURE__*/React.createElement(JrButton, {
    label: cta,
    href: ctaHref
  })) : null)));
}
function JournalPage() {
  const [token] = React.useState(jrReadToken);
  const [state, setState] = React.useState(token ? 'loading' : 'nolink');
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(null);

  // LIVE (2026-10-21): the editor writes a few minutes after the member goes quiet, so the open
  // page re-fetches every 15s while visible; entries that arrive while it is open fade in with a
  // handwritten "novo". The first load marks everything already there as seen.
  const seen = React.useRef(null);
  const [fresh, setFresh] = React.useState({});
  const stamp = React.useRef('');
  React.useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    const load = first => fetch('/cdn/shop/files/jr-' + token + '.json?v=' + Date.now()).then(r => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    }).then(j => {
      if (!alive || !first && j.updatedAt && j.updatedAt === stamp.current) return;
      stamp.current = j.updatedAt || '';
      const ids = (j.entries || []).map(e => e.id);
      if (seen.current === null) {
        seen.current = new Set(ids);
      } else {
        const add = {};
        ids.forEach(id => {
          if (!seen.current.has(id)) {
            add[id] = true;
            seen.current.add(id);
          }
        });
        if (Object.keys(add).length) setFresh(f => ({
          ...f,
          ...add
        }));
      }
      setData(j);
      setState(j.v >= 4 && (j.entries || []).length || j.count > 0 || (j.chapters || []).length || j.v >= 3 && (j.items || []).length ? 'ok' : 'empty');
    }).catch(err => {
      if (!first) return; // a failed refresh keeps what is on screen
      // 404 = the link is unknown (or the file was removed): treat as "no link" and
      // forget it on this device, so the fallback can fetch a fresh one.
      if (String(err.message) === '404') {
        try {
          localStorage.removeItem(JR_STORE_KEY);
        } catch (e) {}
        setState('nolink');
      } else {
        setState('error');
      }
    });
    load(true);
    const iv = setInterval(() => {
      if (!document.hidden) load(false);
    }, 15000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [token]);
  const forget = () => {
    try {
      localStorage.removeItem(JR_STORE_KEY);
    } catch (e) {}
    window.location.reload();
  };
  if (state === 'loading') {
    return /*#__PURE__*/React.createElement(JrNotice, {
      title: "Otvaram tvoj Journal…"
    });
  }
  if (state === 'nolink') {
    return /*#__PURE__*/React.createElement(JrNotice, {
      title: "Journal otvaraš linkom od Boni.",
      body: "Tvoj Journal je privatan, pa ga otvara samo tvoj osobni link. Napiši Boni na WhatsAppu i odmah ti ga pošalje.",
      cta: "Zatraži link na WhatsAppu",
      ctaHref: JR_WA
    });
  }
  if (state === 'error') {
    return /*#__PURE__*/React.createElement(JrNotice, {
      title: "Journal se trenutno ne može otvoriti.",
      body: "Tvoji trenuci su spremljeni i ništa se nije izgubilo. Pokušaj ponovno za minutu."
    });
  }
  const entries = data && data.entries || [];
  const groups = jrGroupByMonth(entries);
  const chapters = null; // v2 chapter snapshots are retired; v3 renders the board
  const demo = !!(data && data.demo);
  if (state === 'ok' && data && data.v >= 4) return /*#__PURE__*/React.createElement(JvDiary, {
    data: data,
    fresh: fresh
  });
  if (state === 'ok' && data && data.v >= 3) {
    return /*#__PURE__*/React.createElement(JrBoard, {
      data: data,
      demo: demo,
      forget: forget,
      token: token
    });
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("section", {
    className: "bh-section bh-section--wave",
    style: {
      background: 'var(--white)'
    }
  }, /*#__PURE__*/React.createElement(Sparkle, {
    size: 20,
    color: "var(--coral-400)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 70,
      left: '6%'
    }
  }), /*#__PURE__*/React.createElement(PawMark, {
    size: 110,
    rotate: 16,
    opacity: 0.08,
    color: "var(--teal-600)",
    className: "bh-doodle",
    style: {
      position: 'absolute',
      top: 40,
      right: -18
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "bh-jr-head"
  }, data && data.dog ? /*#__PURE__*/React.createElement("span", {
    className: "bh-jr-dog"
  }, '🐾 ', data.dog) : null, /*#__PURE__*/React.createElement("h1", {
    className: "bh-jr-h1"
  }, 'Vaš ', /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'relative',
      display: 'inline-block'
    }
  }, "Journal", /*#__PURE__*/React.createElement(Squiggle, {
    color: "var(--coral-400)",
    width: "100%",
    height: 15,
    style: {
      position: 'absolute',
      left: 0,
      bottom: -7,
      width: '100%'
    }
  }))), /*#__PURE__*/React.createElement("p", {
    className: "bh-jr-sub"
  }, state === 'empty' ? 'Još je prazan. Pričaj Boni o svom psu na WhatsAppu ili joj pošalji fotografiju, a ona će ovdje pisati vaš dnevnik.' : chapters ? 'Ti pričaš Boni, a ona od toga slaže vaš album: priče, bilješke i sve važne prve trenutke.' : jrMoments(entries.length) + ' spremljeno. Svaka fotografija koju pošalješ Boni stiže ovdje.'), chapters && data.since ? /*#__PURE__*/React.createElement("p", {
    className: "bh-jr-since"
  }, data.since) : null, demo ? /*#__PURE__*/React.createElement("p", {
    className: "bh-jr-demo"
  }, "Demo: izmišljeni pas i razgovor, da se vidi kako Journal izgleda nakon mjesec dana.") : null, state === 'empty' ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 22
    }
  }, /*#__PURE__*/React.createElement(JrButton, {
    label: "Pošalji fotografiju",
    href: "https://wa.me/385976128956"
  })) : null)), /*#__PURE__*/React.createElement(Wave, {
    fill: "var(--coral-50)",
    variant: 2
  })), state === 'ok' && !chapters ? /*#__PURE__*/React.createElement("section", {
    className: "bh-section",
    style: {
      background: 'var(--coral-50)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bh-wrap"
  }, groups.map(g => /*#__PURE__*/React.createElement("div", {
    key: g.key,
    className: "bh-jr-month"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "bh-jr-mh"
  }, g.key), /*#__PURE__*/React.createElement("div", {
    className: "bh-jr-grid"
  }, g.items.map((e, i) => /*#__PURE__*/React.createElement(JrCard, {
    key: e.id || i,
    e: e,
    i: i,
    onOpen: setOpen
  }))))), /*#__PURE__*/React.createElement("p", {
    className: "bh-jr-foot"
  }, 'Ovo je tvoj privatni Journal. Na zajedničkom uređaju? ', /*#__PURE__*/React.createElement("a", {
    onClick: forget,
    className: "bh-jr-forget"
  }, "Zaboravi ovaj uređaj")))) : null, open ? /*#__PURE__*/React.createElement(JrLightbox, {
    e: open,
    onClose: () => setOpen(null)
  }) : null);
}
window.JournalPage = JournalPage;
// Section order = the message spine (see boni_brand_core.md §2):
//   the promise → the villain → the three pillars → the ask.
// Brands / HowItWorks / Testimonials are still DEFINED in Sections*.jsx but are
// no longer rendered (2026-09-12 repositioning): the three pillar sections
// replace HowItWorks, and the testimonials still describe a bandana and a
// shelter visit that never happened. Re-adding any of them is one line here.
// HEADER IS SHARED CHROME: theme.liquid mounts window.Header (and window.Announce)
// with NO props on every non-index page, so `minimal` is homepage-only by
// construction — the catalog, product and cart pages keep the full header,
// cart icon included. Same reason <Announce /> is simply absent here rather
// than deleted from Sections.jsx: the shop pages still render it.
// Homepage sells the membership, not a basket (2026-09-12).
function App() {
  return /*#__PURE__*/React.createElement("div", {
    id: "top"
  }, /*#__PURE__*/React.createElement(Header, {
    minimal: true
  }), /*#__PURE__*/React.createElement(Hero, null), /*#__PURE__*/React.createElement(Villain, null), /*#__PURE__*/React.createElement(Journal, null), /*#__PURE__*/React.createElement(HowBridge, null), /*#__PURE__*/React.createElement(JoinClose, null), /*#__PURE__*/React.createElement(Footer, null));
}
window.App = App;
